/**
 * DoctorWebsiteMockB — second variant at /dr/:slug/preview-b
 *
 * What's different vs Mock A:
 *  1. Theme palette is derived from doctor.experience_years using DocSite's
 *     5-stage life-stage system (sky / emerald / amber / violet / rose).
 *     Stage propagates via CSS custom properties so every accent (CTAs,
 *     ribbons, sub-nav underline, rating fills, etc) stays in lockstep.
 *  2. Booking widget OCCUPIES the position where the photo was in Mock A —
 *     Zocdoc-style "Book an appointment for free" right-column card with the
 *     doctor's small avatar + name + 3-day slot peek + big stage-coloured CTA.
 *  3. Tighter, balanced type scale — body lands at 1rem-1.15rem, headings at
 *     clamp(2.5rem, 5vw, 4.25rem). No 200px hero monsters; reads more like
 *     Zocdoc's actual scale while still being confident.
 *
 * Override stage for QA: append ?stage=01..05 to the URL.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import MonogramAvatar, { computeMonogram } from '@/components/MonogramAvatar';
import InstallQR from '@/components/InstallQR';
import ClinicMap from '@/components/ClinicMap';
import DistanceToClinic from '@/components/DistanceToClinic';
import SaveMyDetailsWidget from '@/components/SaveMyDetailsWidget';
import RequestVoiceCallModal from '@/components/public/RequestVoiceCallModal';
import DoctorBookingFlow from '@/components/DoctorBookingFlow';
import QRCode from 'qrcode';
import {
  MapPin, Award, Calendar, CheckCircle, Star, Shield, MessageCircle, Phone,
  ArrowRight, Building2, Stethoscope, Globe, Sparkles, Heart, Clock, Download,
  ExternalLink, ChevronDown, ChevronRight, Lock, Smartphone, Hospital, BadgeCheck, Users, X, CreditCard, Loader2 as Loader,
} from 'lucide-react';

// 5-stage palette tied to years of practice (DocSite branding norm)
const STAGE_PALETTES = {
  '01': { id: '01', label: 'First Practice',         range: '0–3 yrs',
          accent: '#0EA5E9', accentDark: '#075985', soft: '#E0F2FE', mid: '#7DD3FC',
          band: 'linear-gradient(135deg,#E0F2FE 0%,#F0F9FF 60%,#FFFFFF 100%)' },
  '02': { id: '02', label: 'Established Practice',   range: '4–10 yrs',
          accent: '#10B981', accentDark: '#065F46', soft: '#D1FAE5', mid: '#6EE7B7',
          band: 'linear-gradient(135deg,#D1FAE5 0%,#ECFDF5 60%,#FFFFFF 100%)' },
  '03': { id: '03', label: 'Mastery & Innovation',   range: '11–20 yrs',
          accent: '#F59E0B', accentDark: '#92400E', soft: '#FEF3C7', mid: '#FCD34D',
          band: 'linear-gradient(135deg,#FEF3C7 0%,#FFFBEB 60%,#FFFFFF 100%)' },
  '04': { id: '04', label: 'Public Footprint',       range: '21–30 yrs',
          accent: '#8B5CF6', accentDark: '#5B21B6', soft: '#EDE9FE', mid: '#C4B5FD',
          band: 'linear-gradient(135deg,#EDE9FE 0%,#F5F3FF 60%,#FFFFFF 100%)' },
  '05': { id: '05', label: 'Practicing Emeritus',    range: '30+ yrs',
          accent: '#F43F5E', accentDark: '#9F1239', soft: '#FFE4E6', mid: '#FDA4AF',
          band: 'linear-gradient(135deg,#FFE4E6 0%,#FFF1F2 60%,#FFFFFF 100%)' },
};

function stageFromYears(years) {
  const y = Number(years || 0);
  if (y <= 3)  return STAGE_PALETTES['01'];
  if (y <= 10) return STAGE_PALETTES['02'];
  if (y <= 20) return STAGE_PALETTES['03'];
  if (y <= 30) return STAGE_PALETTES['04'];
  return STAGE_PALETTES['05'];
}

const SECTION_TABS = [
  { id: 'highlights', label: 'Highlights' },
  { id: 'book',       label: 'Book' },
  { id: 'about',      label: 'About' },
  { id: 'procedures', label: 'Procedures' },
  { id: 'locations',  label: 'Locations' },
  { id: 'reviews',    label: 'Reviews' },
  { id: 'cases',      label: 'Case Studies' },
  { id: 'articles',   label: 'Articles' },
  { id: 'faqs',       label: 'FAQs' },
];

export default function DoctorWebsiteMockB({ productionMode = false }) {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('highlights');
  const [scrolled, setScrolled] = useState(false);
  const [activeLoc, setActiveLoc] = useState(0);
  const [caseStudies, setCaseStudies] = useState([]);
  const [articles, setArticles] = useState([]);
  const [archiveStudies, setArchiveStudies] = useState([]);
  const [showArchive, setShowArchive] = useState(false);
  const [hasArchive, setHasArchive] = useState(false);

  useEffect(() => {
    api.get(`/doctor/${slug}`).then(r => setDoctor(r.data)).finally(() => setLoading(false));
    // Case studies (recent + archive availability flag)
    api.get(`/doctor/${slug}/case-studies`)
      .then(r => {
        setCaseStudies(r.data.recent || []);
        setHasArchive(r.data.has_archive || false);
      })
      .catch(() => {});
    // Doctor-approved AI SEO articles
    api.get(`/public/doctor/${slug}/articles`)
      .then(r => setArticles(r.data.articles || []))
      .catch(() => {});
  }, [slug]);

  const loadArchive = async () => {
    try {
      const r = await api.get(`/doctor/${slug}/case-studies`, { params: { include_archive: 'true' } });
      const all = r.data.recent || [];
      const archive = r.data.archive || [];
      const recentIds = new Set(caseStudies.map(c => c.id));
      setArchiveStudies(archive.length > 0 ? archive : all.filter(c => !recentIds.has(c.id)));
      setShowArchive(true);
    } catch {/* show nothing */}
  };

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 360);
      const found = SECTION_TABS.find(t => {
        const el = document.getElementById(`sec-${t.id}`);
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return r.top <= 130 && r.bottom > 130;
      });
      if (found) setActiveTab(found.id);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const stage = useMemo(() => {
    const forced = params.get('stage');
    if (forced && STAGE_PALETTES[forced]) return STAGE_PALETTES[forced];
    return stageFromYears(doctor?.experience_years);
  }, [doctor, params]);

  // Privacy-first default: never expose a doctor's personal WhatsApp number
  // unless the backend `calls_chat` payload explicitly opts in. Falling back
  // to `false` here matches the server-side default in `calls_chat_summary()`
  // and stops a doctor's mobile leaking on profiles created before the
  // setting existed (most older accounts).
  const cc = doctor?.calls_chat || { turbo_active: false, calls_enabled: false, chat_enabled: false, whatsapp_personal_enabled: false, call_route: 'doctor', receptionist_name: null };
  const [callModal, setCallModal] = useState(null); // {state: 'locked'} - kept for chat-locked case; voice now uses voiceCallOpen
  const [chatModal, setChatModal] = useState(null); // {state: 'connecting'|'locked'}
  const [voiceCallOpen, setVoiceCallOpen] = useState(false);
  const [selectedBookingDate, setSelectedBookingDate] = useState(new Date());
  const [bookingMode, setBookingMode] = useState('in_person'); // 'in_person' | 'video'
  const [bookingToast, setBookingToast] = useState('');

  const handleCall = async () => {
    if (!cc.calls_enabled) {
      setCallModal({ state: 'locked', kind: 'call' });
      return;
    }
    // Open the real WebRTC voice call modal
    setVoiceCallOpen(true);
  };
  const proceedCallAfterAdvisory = async () => {
    // Legacy stub kept for backward compat with any older callers; just opens the modal.
    setVoiceCallOpen(true);
    setCallModal(null);
  };
  const handleChat = async () => {
    // In-app chat is now universally available (no tier gate). The chat panel
    // creates the patient↔doctor thread on first message.
    setChatModal({ state: 'connecting' });
    try {
      await api.post('/dashboard/calls-chat/intent/chat', { slug });
    } catch { /* intent record is best-effort */ }
  };

  const cssVars = useMemo(() => ({
    '--brand': stage.accent,
    '--brand-dark': stage.accentDark,
    '--brand-soft': stage.soft,
    '--brand-mid': stage.mid,
  }), [stage]);

  const scrollTo = (id) => {
    const el = document.getElementById(`sec-${id}`);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400 text-lg">Loading…</div>;
  if (!doctor) return <div className="min-h-screen flex items-center justify-center text-slate-500">Doctor not found</div>;

  const photoSrc = doctor.photo_url?.startsWith('/api/')
    ? `${process.env.REACT_APP_BACKEND_URL || ''}${doctor.photo_url}`
    : doctor.photo_url;
  const primarySpec = (doctor.specialization_names || [])[0] || 'Doctor';
  const primaryLoc = (doctor.locations || [])[activeLoc] || (doctor.locations || [])[0];
  const firstName = doctor.name?.replace(/^Dr\.?\s*/i, '').split(' ')[0] || 'Doctor';

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased" style={cssVars} data-testid="mock-b-root">
      <Helmet>
        {!productionMode && <title>{`[Mock B] Dr. ${doctor.name?.replace(/^Dr\.?\s*/i, '')} — Stage ${stage.id} · DocSite preview`}</title>}
        {!productionMode && <meta name="robots" content="noindex" />}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      </Helmet>

      {!productionMode && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-xs text-center py-2 font-medium" data-testid="mock-b-preview-banner">
          Preview · Mock B · Stage {stage.id} {stage.label} ({stage.range}) · production page is at /dr/{slug}
        </div>
      )}

      {/* STICKY HEADER — slim, Zocdoc-like */}
      <header className={`sticky top-0 z-40 bg-white/95 backdrop-blur-md transition-all duration-300 ${scrolled ? 'border-b border-slate-200 shadow-sm' : 'border-b border-transparent'}`} data-testid="mock-b-header">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="font-semibold text-base tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
            Dr. {doctor.name?.replace(/^Dr\.?\s*/i, '')}
          </Link>
          {scrolled && (
            <div className="flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
              {photoSrc ? (
                <img src={photoSrc} alt="" className="w-9 h-9 rounded-full object-cover" />
              ) : (
                <div className="w-9 h-9">
                  {(() => { const m = computeMonogram(doctor.name, doctor.specializations); return <MonogramAvatar initials={m.initials} bgColor={m.bg_color} size={36} />; })()}
                </div>
              )}
              <div className="hidden sm:flex flex-col text-right">
                <span className="text-xs text-slate-500 font-medium">{primarySpec}</span>
                {doctor.rating?.score && (
                  <span className="text-xs font-semibold text-amber-600 flex items-center gap-1 justify-end">
                    <Star size={11} fill="currentColor" /> {doctor.rating.score}/5
                  </span>
                )}
              </div>
              <button onClick={() => scrollTo('book')}
                className="rounded-full px-5 py-2 text-sm font-semibold text-white transition-all hover:scale-105"
                style={{ backgroundColor: 'var(--brand)' }}
                data-testid="mock-b-sticky-book-btn">
                View availability
              </button>
            </div>
          )}
          {!scrolled && (
            <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600 font-medium">
              {SECTION_TABS.slice(0, 5).map(t => (
                <button key={t.id} onClick={() => scrollTo(t.id)}
                  className="hover:text-slate-900 transition-colors">{t.label}</button>
              ))}
              <button onClick={() => scrollTo('book')}
                className="rounded-full px-5 py-2 text-sm font-semibold text-white"
                style={{ backgroundColor: 'var(--brand)' }}>
                Book Appointment
              </button>
            </nav>
          )}
        </div>
      </header>

      {/* HERO — left text · right booking widget (replaces photo) */}
      <section className="relative overflow-hidden" style={{ background: stage.band }}>
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 pt-6 pb-8 md:pt-8 md:pb-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
            {/* TEXT */}
            <div className="lg:col-span-7 flex flex-col space-y-4">
              {/* Stage badge */}
              <div className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5"
                   style={{ backgroundColor: stage.soft, border: `1px solid ${stage.mid}` }}
                   data-testid="mock-b-stage-badge">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: stage.accentDark }}>
                  {stage.label}
                </span>
                <span className="text-[10px] font-medium" style={{ color: stage.accentDark }}>
                  · {doctor.experience_years || 0} yrs in practice
                </span>
              </div>

              {(doctor.specialization_names || []).length > 0 && (
                <div className="text-sm md:text-base uppercase tracking-[0.2em] font-bold" style={{ color: 'var(--brand-dark)' }}>
                  {doctor.specialization_names.join(' · ')}
                </div>
              )}

              <h1
                className="mockB-h-base font-medium leading-[1] tracking-tight text-slate-900"
                style={{
                  fontSize: 'clamp(2rem, 5vw, 4.25rem)',
                  letterSpacing: '-0.02em',
                }}
                data-testid="mock-b-hero-name"
              >
                {doctor.name}
              </h1>

              {doctor.affiliations && (
                <p
                  className="text-slate-700 font-medium max-w-2xl"
                  style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(1rem, 1.2vw, 1.2rem)', letterSpacing: '-0.005em' }}
                  data-testid="mock-b-hero-affiliations"
                >
                  {doctor.affiliations}
                </p>
              )}

              {doctor.tagline && (
                <p className="text-slate-600 italic max-w-2xl"
                   style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.25rem, 1.7vw, 1.55rem)' }}>
                  &ldquo;{doctor.tagline}&rdquo;
                </p>
              )}

              <p className="text-slate-700 max-w-2xl leading-[1.55]" style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(1.1rem, 1.3vw, 1.3rem)' }}>
                {doctor.qualifications}
                {doctor.registration_number && (
                  <span className="block text-sm md:text-base text-slate-500 mt-2.5 uppercase tracking-widest font-semibold">
                    NMC / State Medical Council Reg · {doctor.registration_number}
                  </span>
                )}
              </p>

              {/* Stat row — buttons get a visible underline + chevron on hover so
                  patients realise they're clickable jump-links into the page. */}
              <div className="flex flex-wrap gap-x-9 gap-y-3 pt-4 text-slate-600" style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(1rem, 1.2vw, 1.2rem)' }}>
                <span className="flex items-center gap-2"><Award size={18} style={{ color: 'var(--brand)' }} /><strong className="text-slate-900 text-xl md:text-2xl">{doctor.experience_years || 0}</strong>&nbsp;years</span>
                <button onClick={() => scrollTo('locations')} className="group flex items-center gap-2 hover:text-slate-900 hover:underline underline-offset-4 decoration-2 transition-colors cursor-pointer" style={{ textDecorationColor: stage.accent }} data-testid="mock-b-stat-clinics">
                  <Building2 size={18} style={{ color: 'var(--brand)' }} /><strong className="text-slate-900 text-xl md:text-2xl">{(doctor.locations || []).length}</strong>&nbsp;{(doctor.locations || []).length === 1 ? 'clinic' : 'clinics'}
                  <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 -ml-1 transition-opacity" style={{ color: stage.accentDark }} />
                </button>
                {(doctor.hospital_attachments || []).length > 0 && (
                  <button onClick={() => scrollTo('locations')} className="group flex items-center gap-2 hover:text-slate-900 hover:underline underline-offset-4 decoration-2 transition-colors cursor-pointer" style={{ textDecorationColor: stage.accent }} data-testid="mock-b-stat-hospitals">
                    <Hospital size={18} style={{ color: 'var(--brand)' }} /><strong className="text-slate-900 text-xl md:text-2xl">{(doctor.hospital_attachments || []).length}</strong>&nbsp;{(doctor.hospital_attachments || []).length === 1 ? 'hospital' : 'hospitals'}
                    <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 -ml-1 transition-opacity" style={{ color: stage.accentDark }} />
                  </button>
                )}
                <button onClick={() => scrollTo('procedures')} className="group flex items-center gap-2 hover:text-slate-900 hover:underline underline-offset-4 decoration-2 transition-colors cursor-pointer" style={{ textDecorationColor: stage.accent }} data-testid="mock-b-stat-procedures">
                  <Stethoscope size={18} style={{ color: 'var(--brand)' }} /><strong className="text-slate-900 text-xl md:text-2xl">{(doctor.procedures || []).length}</strong>&nbsp;{(doctor.procedures || []).length === 1 ? 'procedure' : 'procedures'}
                  <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 -ml-1 transition-opacity" style={{ color: stage.accentDark }} />
                </button>
                {doctor.rating?.score && (
                  <span className="flex items-center gap-2"><Star size={18} className="text-amber-500" fill="currentColor" /><strong className="text-slate-900 text-xl md:text-2xl">{doctor.rating.score}</strong>/5</span>
                )}
              </div>

              {/* Trained-at small inline */}
              {doctor.trained_at?.hospital_name && (
                <div className="inline-flex items-center gap-2 mt-3 rounded-lg px-3 py-2 bg-white border border-slate-200" data-testid="mock-b-trained-at">
                  <Award size={14} className="text-amber-600" />
                  <span className="text-xs text-slate-700">
                    <span className="font-bold uppercase tracking-widest text-amber-700 mr-1.5 text-[10px]">Trained at</span>
                    <span className="font-semibold">{doctor.trained_at.hospital_name}</span>
                    {doctor.trained_at.degree && <span className="text-slate-500"> · {doctor.trained_at.degree}{doctor.trained_at.year ? `, ${doctor.trained_at.year}` : ''}</span>}
                  </span>
                </div>
              )}

              {/* Above-the-fold QR — bottom-aligned to the booking card on the
                  right; spans the same width as the stat row above so the
                  left and right borders of the card line up with the stat
                  row content. */}
              <div className="hidden lg:flex flex-1 items-end pt-2">
                <CompactInstallQrCard slug={slug} stage={stage} firstName={firstName} doctorName={doctor.name} testId="mock-b-hero-qr-card" />
              </div>
            </div>

            {/* RIGHT COLUMN — Booking widget replacing the photo (Zocdoc-style).
                On desktop the booking card stays sticky inside the hero
                column so it remains visible as the user reads the rest of
                the profile, but stays bound by `items-stretch` so its
                height matches the left column. */}
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-20">
              <BookingHeroCard doctor={doctor} stage={stage} primarySpec={primarySpec} firstName={firstName} photoSrc={photoSrc} cc={cc} slug={slug}
                               bookingMode={bookingMode} setBookingMode={setBookingMode}
                               onPickDate={(d) => { setSelectedBookingDate(d); scrollTo('book'); }}
                               onPickSlot={(d) => { setSelectedBookingDate(d); scrollTo('book'); }}
                               onShowToast={setBookingToast}
                               onSeeAll={() => scrollTo('book')} onCall={handleCall} onChat={handleChat} />

              {/* Mobile-only fallback: stack the QR card under the booking card so phone users still see it */}
              <div className="lg:hidden mt-4 rounded-2xl border-2 bg-white p-5"
                   style={{ borderColor: stage.mid }}
                   data-testid="mock-b-hero-qr-card-mobile">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: stage.soft }}>
                    <Smartphone size={18} style={{ color: stage.accentDark }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: stage.accentDark }}>
                      Patient · scan to install &amp; connect
                    </p>
                    <p className="font-semibold text-slate-900 text-lg mt-1 leading-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Point your phone — install DocSite + connect to Dr. {firstName} + get your MyConnect Portal for future bookings
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex justify-center">
                  <InstallQR
                    url={`${(typeof window !== 'undefined' ? window.location.origin : 'https://docsite.care')}/c/${slug}`}
                    title=""
                    subtitle=""
                    variant="card"
                    testId="mock-b-hero-qr-mobile"
                  />
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SUB-NAV */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur-md border-b border-slate-200" data-testid="mock-b-subnav">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="flex items-center gap-9 overflow-x-auto scrollbar-hide h-14">
            {SECTION_TABS.map(t => (
              <button key={t.id} onClick={() => scrollTo(t.id)}
                className={`relative whitespace-nowrap text-base md:text-lg font-semibold transition-colors ${activeTab === t.id ? 'text-slate-900' : 'text-slate-500 hover:text-slate-800'}`}
                style={{ fontFamily: 'Inter, sans-serif' }}
                data-testid={`mock-b-tab-${t.id}`}>
                {t.label}
                {activeTab === t.id && <span className="absolute -bottom-[15px] left-0 right-0 h-[3px]" style={{ backgroundColor: 'var(--brand)' }} />}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* MAIN GRID */}
      <main className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 py-10 md:py-14">
        {/* Single-column full-width flow. Booking widget renders inline as its
            own full-width section so it commands attention without reserving
            33% of every page for a sticky sidebar (which leaves long content
            sections — About, Procedures, Locations — visually narrowed). */}
        <div className="space-y-14 min-w-0">
          {/* HIGHLIGHTS */}
          <Section id="highlights" eyebrow="At a glance" title="Highlights" stage={stage}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Tile icon={<Heart size={22} className="text-rose-500" />} title="Patients return" body="Repeat-visit rate above the city average for this specialty — patients trust the outcomes." />
              <Tile icon={<Sparkles size={22} className="text-amber-500" />} title="Highly recommended" body={doctor.rating?.score ? `${Math.round((doctor.rating.score / 5) * 100)}% of patients rated 4★ or above on bedside manner.` : 'Bedside-manner ratings will appear once 5+ verified reviews are in.'} />
              <Tile icon={<Clock size={22} className="text-sky-500" />} title="On-time clinic" body="Average wait under 20 minutes — schedules are honoured via DocSite's slot governance." />
              <Tile icon={<CheckCircle size={22} className="text-emerald-500" />} title="New patients welcome" body="Accepting new patients on every clinic day. Same-day slots open by 8 AM." />
            </div>
          </Section>

          {/* BOOKING — promoted from the right-rail to a full-width section so
              every visitor sees it without scrolling sideways. `items-start`
              prevents the sibling card from stretching to match the booking
              flow's height when the doctor's schedule is sparse. */}
          <Section id="book" eyebrow="Book online" title={`Book Dr. ${firstName}`} stage={stage}>
            <div className="grid lg:grid-cols-2 gap-6 items-start">
              <DoctorBookingFlow
                doctor={doctor}
                slug={slug}
                stage={stage}
                controlledDate={selectedBookingDate}
              />
              <BookingSecondaryCard stage={stage} firstName={firstName} doctor={doctor} cc={cc} onCall={handleCall} onChat={handleChat} />
            </div>
          </Section>


            {/* ABOUT */}
            <Section id="about" eyebrow={`About Dr. ${firstName}`} title={`Why patients choose Dr. ${firstName}`} stage={stage}>
              <div className="flex flex-col sm:flex-row gap-6 items-start">
                {photoSrc ? (
                  <img src={photoSrc} alt={doctor.name}
                       className="w-40 h-52 rounded-2xl object-cover border shadow-md shrink-0"
                       style={{ borderColor: stage.mid }}
                       data-testid="mock-b-about-photo" />
                ) : (
                  <div className="w-40 h-52 rounded-2xl overflow-hidden border shadow-md shrink-0 flex items-center justify-center"
                       style={{ borderColor: stage.mid, backgroundColor: stage.soft }}
                       data-testid="mock-b-about-photo-fallback">
                    <div className="w-28">
                      {(() => { const m = computeMonogram(doctor.name, doctor.specializations); return <MonogramAvatar initials={m.initials} bgColor={m.bg_color} size={112} />; })()}
                    </div>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-slate-700 leading-[1.7]" style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(1.05rem, 1.2vw, 1.2rem)' }}>
                    {doctor.bio || <span className="italic text-slate-400">Bio coming soon — Dr. {firstName} hasn't published their About section yet.</span>}
                  </p>
                  {doctor.voice_average_pct >= 40 && (
                    <div className="mt-5 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold"
                         style={{ backgroundColor: stage.soft, border: `1px solid ${stage.mid}`, color: stage.accentDark }}
                         data-testid="mock-b-voice-badge">
                      <Sparkles size={12} /> Authored by Dr. {firstName} · Voice {doctor.voice_average_pct}%
                    </div>
                  )}
                </div>
              </div>
            </Section>

            {/* PROCEDURES */}
            <Section id="procedures" eyebrow="What I treat" title="Procedures & treatments" stage={stage}>
              <ProceduresGrid procedures={doctor.procedures || []} stage={stage} slug={slug} />
            </Section>

            {/* CLINICS & HOSPITAL INFRA — consultation clinics + surgical hospital attachments together */}
            <Section id="locations" eyebrow="Where I see &amp; operate" title="Clinics &amp; Hospital Infra" stage={stage}>
              {(doctor.locations || []).length > 1 && (
                <div className="flex gap-2 mb-4 flex-wrap">
                  {doctor.locations.map((l, i) => (
                    <button key={l.id || i} onClick={() => setActiveLoc(i)}
                      className={`rounded-full px-4 py-1.5 text-sm font-semibold border transition-all`}
                      style={activeLoc === i
                        ? { backgroundColor: stage.accentDark, color: 'white', borderColor: stage.accentDark }
                        : { backgroundColor: 'white', color: '#475569', borderColor: '#cbd5e1' }}
                      data-testid={`mock-b-loc-tab-${i}`}>
                      {l.name || l.clinic_name || `Location ${i + 1}`}
                    </button>
                  ))}
                </div>
              )}
              {primaryLoc && (
                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                  <ClinicMap
                    locationId={primaryLoc.id}
                    name={primaryLoc.name || primaryLoc.clinic_name}
                    address={[primaryLoc.address, primaryLoc.city].filter(Boolean).join(', ')}
                    pincode={primaryLoc.pincode}
                    height={320}
                  />
                  <div className="p-5 md:p-7 grid md:grid-cols-2 gap-5">
                    <div>
                      <p className="mockB-h-base font-semibold text-slate-900" style={{ fontSize: 'clamp(1.05rem, 1.5vw, 1.4rem)', fontWeight: 600 }}>
                        {primaryLoc.name || primaryLoc.clinic_name}
                      </p>
                      <p className="text-slate-600 mt-2 leading-relaxed text-base">
                        {[primaryLoc.address, primaryLoc.city, primaryLoc.pincode].filter(Boolean).join(', ')}
                      </p>
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([primaryLoc.address, primaryLoc.city].filter(Boolean).join(', '))}`}
                         target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1.5 mt-3 text-base font-semibold hover:underline"
                         style={{ color: stage.accentDark }}>
                        Get directions <ExternalLink size={13} />
                      </a>
                      <div>
                        <DistanceToClinic key={primaryLoc.id} locationId={primaryLoc.id} accentColor={stage.accent} />
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Clinic hours</p>
                      {(primaryLoc.availability_mode || 'scheduled') === 'on_request_only' ? (
                        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 space-y-2" data-testid={`on-request-block-${primaryLoc.id}`}>
                          <p className="text-[11px] uppercase tracking-widest font-bold text-amber-700">
                            By appointment — on request only
                          </p>
                          {primaryLoc.role_line && (
                            <p className="text-[12px] font-semibold text-violet-700">{primaryLoc.role_line}</p>
                          )}
                          <p className="text-[12px] text-slate-700 leading-snug">
                            Dr. {firstName} visits this hospital <b>exclusively when a patient asks</b>. Submit a request — they'll offer a 30-min window in real-time.
                          </p>
                          <a href={`/dr/${slug}/request-appointment?location_id=${primaryLoc.id}`}
                             className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white hover:opacity-90 transition-opacity"
                             style={{ backgroundColor: '#d97706' }}
                             data-testid={`request-appointment-cta-${primaryLoc.id}`}>
                            Request an appointment →
                          </a>
                        </div>
                      ) : (
                        <ClinicHours schedules={doctor.schedules || []} locationId={primaryLoc.id} />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Surgical Infrastructure — hospital attachments where major procedures happen */}
              {(doctor.hospital_attachments || []).length > 0 && (
                <div className="mt-10" data-testid="mock-b-surgical-infra">
                  <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1.5" style={{ color: stage.accentDark }}>
                        Hospital attachments · {(doctor.hospital_attachments || []).length}
                      </p>
                      <h3 className="mockB-h-base font-medium leading-tight tracking-tight text-slate-900"
                          style={{ fontSize: 'clamp(1.15rem, 2.4vw, 2rem)' }}>
                        Surgical infrastructure for major procedures
                      </h3>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {doctor.hospital_attachments.map((h, i) => (
                      <HospitalAttachmentCard key={h.id || i} attachment={h} doctor={doctor} stage={stage} />
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* REVIEWS — DocSite TQS authentic rating engine */}
            <Section id="reviews" eyebrow="Treatment Quality Score" title="Patient outcomes — measured, not asked" stage={stage}>
              <TqsBlock slug={slug} stage={stage} firstName={firstName} />
            </Section>

            {/* CASE STUDIES — In Doctor's Own Words. Doctor-authored,
                date-grouped, time-decay archive (older > 3 months collapses
                behind a 'Show archive' button). Voice-Meter ensures the copy
                is genuinely the doctor's writing, not AI boilerplate. */}
            {(caseStudies.length > 0 || hasArchive) && (
              <Section id="cases" eyebrow="In my own words" title="Case studies & clinical wins" stage={stage}>
                <div className="space-y-4">
                  {caseStudies.map(cs => (
                    <CaseStudyCard key={cs.id} cs={cs} stage={stage} firstName={firstName} />
                  ))}
                  {hasArchive && !showArchive && (
                    <button onClick={loadArchive}
                            className="w-full rounded-xl border-2 border-dashed border-slate-300 py-4 text-sm font-semibold text-slate-600 hover:border-slate-500 hover:text-slate-900 transition-colors"
                            data-testid="mock-b-cases-show-archive">
                      Show older case studies →
                    </button>
                  )}
                  {showArchive && archiveStudies.length > 0 && (
                    <div className="space-y-4 pt-2 border-t border-slate-200" data-testid="mock-b-cases-archive">
                      {archiveStudies.map(cs => (
                        <CaseStudyCard key={cs.id} cs={cs} stage={stage} firstName={firstName} archived />
                      ))}
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* AI-WRITTEN SEO ARTICLES — doctor-approved long-form content,
                renders inline so Google indexes 1000s of indexable words on
                /dr/{slug}. Each card links out to /dr/{slug}/article/{slug}
                where the full text + JSON-LD Article schema lives. */}
            {articles.length > 0 && (
              <Section id="articles" eyebrow="Health insights" title={`Articles by Dr. ${firstName}`} stage={stage}>
                <div className="grid sm:grid-cols-2 gap-3">
                  {articles.slice(0, 6).map(a => (
                    <ArticleCard key={a.slug} article={a} stage={stage} doctorSlug={slug} />
                  ))}
                </div>
                {articles.length > 6 && (
                  <Link to={`/dr/${slug}/articles`}
                        className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold hover:underline"
                        style={{ color: stage.accentDark }}
                        data-testid="mock-b-articles-see-all">
                    See all {articles.length} articles →
                  </Link>
                )}
              </Section>
            )}

            {/* FAQs */}
            <Section id="faqs" eyebrow="Common questions" title="Patient FAQs" stage={stage}>
              <div className="space-y-2.5">
                {[
                  { q: 'Do you accept walk-ins?', a: `For best service please book a slot via the panel on the right — Dr. ${firstName} keeps daily walk-in capacity but it can fill quickly.` },
                  { q: 'Are video consults available?', a: 'Yes — both in-person and video visits are bookable through this page.' },
                  { q: 'Can I export my visit history?', a: 'Yes. Your visit history is yours, exportable as PDF any time from your patient profile.' },
                ].map((f, i) => (
                  <details key={i} className="group rounded-xl border border-slate-200 bg-white px-5 py-4 cursor-pointer" data-testid={`mock-b-faq-${i}`}>
                    <summary className="flex items-center justify-between font-semibold text-slate-900 list-none text-[17px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {f.q}
                      <ChevronDown size={16} className="text-slate-400 group-open:rotate-180 transition-transform" />
                    </summary>
                    <p className="text-slate-600 leading-relaxed mt-2.5 text-base">{f.a}</p>
                  </details>
                ))}
              </div>
            </Section>
        </div>
      </main>

      {/* Save My Details — Turbo zero-friction patient registration. Renders
          as a corner-anchored expandable widget; backend gates it on the
          doctor's tier so non-Turbo profiles see a graceful "coming soon"
          state from inside the widget itself. */}
      <SaveMyDetailsWidget doctor={doctor} slug={slug} />

      {voiceCallOpen && (
        <RequestVoiceCallModal slug={slug} doctorName={doctor.name} onClose={() => setVoiceCallOpen(false)} />
      )}

      {bookingToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[110] bg-slate-900 text-white text-sm rounded-full px-5 py-3 shadow-2xl flex items-center gap-2 max-w-[90vw]" data-testid="mock-b-booking-toast">
          <CheckCircle size={14} className="text-emerald-400 shrink-0" />
          <span>{bookingToast}</span>
        </div>
      )}

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-slate-50 mt-10">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 py-9 grid md:grid-cols-2 gap-6 text-sm text-slate-500">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-700 mb-1.5">Patient privacy</p>
            <p className="leading-relaxed">No tracking pixels. No data brokers. Your visit history is yours — exportable any time as a PDF.</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-700 mb-1.5">Powered by</p>
            <Link to="/" className="font-semibold text-slate-900" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.3rem' }}>DocSite</Link>
            <p className="text-xs text-slate-400 mt-0.5">The portable doctor website platform · India</p>
          </div>
        </div>
      </footer>
      {/* Modals — Turbo locked / Call advisory / Mocked-VoIP feedback */}
      {callModal && (
        <CallModal modal={callModal} stage={stage} firstName={firstName} doctor={doctor}
                   onProceed={proceedCallAfterAdvisory} onClose={() => setCallModal(null)} />
      )}
      {chatModal && (
        <ChatModal modal={chatModal} stage={stage} firstName={firstName} slug={slug} onClose={() => setChatModal(null)} />
      )}
    </div>
  );
}

// ====================== Helpers ======================

function ProceduresGrid({ procedures, stage, slug }) {
  const [showAll, setShowAll] = useState(false);
  const [openId, setOpenId] = useState(null);
  const list = showAll ? procedures : procedures.slice(0, 12);
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mock-b-procedures-grid">
        {list.map((p, i) => {
          const isOpen = openId === (p.id || i);
          return (
            <div key={p.id || i} className={isOpen ? 'sm:col-span-2 lg:col-span-3' : ''}>
              <ProcedureCard
                p={p}
                i={i}
                stage={stage}
                slug={slug}
                isOpen={isOpen}
                onToggle={() => setOpenId(isOpen ? null : (p.id || i))}
              />
            </div>
          );
        })}
      </div>
      {procedures.length > 12 && (
        <button onClick={() => setShowAll(!showAll)}
          className="mt-5 text-sm font-semibold hover:underline inline-flex items-center gap-1"
          style={{ color: stage.accentDark }}
          data-testid="mock-b-proc-toggle-all">
          {showAll ? '↑ Show less' : `+ ${procedures.length - 12} more — show all`}
        </button>
      )}
    </>
  );
}

function ProcedureCard({ p, i, stage, isOpen, onToggle }) {
  const catChar = (p.type === 'Lab Test') ? 'L'
    : (p.type === 'Major Surgery' || p.type === 'Minor Procedure') ? 'OT'
    : 'C';
  // Both badges now follow the doctor's stage palette so the page feels
  // cohesive (purple for Chetna's career stage, sky for early-career, etc).
  // Surgery uses a slightly darker accent variant to retain seriousness.
  const isSurgery = p.type === 'Major Surgery';
  const lo = p.price_min, hi = p.price_max;
  let priceLabel = null;
  if (lo != null && hi != null && lo > 0 && hi > 0 && lo !== hi) {
    priceLabel = `Rs. ${Number(lo).toLocaleString('en-IN')} – ${Number(hi).toLocaleString('en-IN')}`;
  } else {
    const single = (lo && lo > 0) ? lo : (hi && hi > 0 ? hi : null);
    priceLabel = single ? `Rs. ${Number(single).toLocaleString('en-IN')}` : 'Price on request';
  }
  const indications = Array.isArray(p.indications) ? p.indications
    : (typeof p.indications === 'string' ? p.indications.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean) : []);
  return (
    <div className={`rounded-xl border bg-white transition-all overflow-hidden h-full ${isOpen ? 'shadow-lg' : 'hover:shadow-md hover:-translate-y-0.5'}`}
         style={{ borderColor: isOpen ? stage.accent : '' }}
         data-testid={`mock-b-proc-${i}`}>
      <button onClick={onToggle}
        className={`w-full text-left cursor-pointer ${isOpen ? 'p-6' : 'p-4'}`}
        aria-expanded={isOpen}
        data-testid={`mock-b-proc-toggle-${i}`}>
        <div className={`flex items-start ${isOpen ? 'gap-4' : 'gap-3'}`}>
          <div className={`${isOpen ? 'w-12 h-12' : 'w-9 h-9'} rounded-lg flex items-center justify-center shrink-0`} style={{ backgroundColor: stage.soft }}>
            <Stethoscope size={isOpen ? 22 : 16} style={{ color: stage.accentDark }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <p className={`font-semibold text-slate-900 leading-tight ${isOpen ? 'text-2xl sm:text-3xl' : 'text-[16px]'}`} style={{ fontFamily: 'Inter, sans-serif' }}>{p.name}</p>
              <span className={`shrink-0 ${isOpen ? 'w-9 h-9 text-sm' : 'w-6 h-6 text-[10px]'} rounded-full font-bold flex items-center justify-center leading-none`}
                    style={{ backgroundColor: stage.soft, color: stage.accentDark }}
                    title={catChar === 'C' ? 'Consultation' : catChar === 'L' ? 'Lab Test' : 'Operation Theatre / Procedure'}>
                {catChar}
              </span>
            </div>
            {p.description && !isOpen && (
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed line-clamp-2" data-testid={`mock-b-proc-desc-${i}`}>
                {p.description}
              </p>
            )}
            <div className={`flex items-center flex-wrap gap-2 ${isOpen ? 'mt-4' : 'mt-2.5'}`}>
              {p.type && (
                <span className={`rounded-full font-semibold ${isOpen ? 'px-3 py-1 text-xs' : 'px-2 py-0.5 text-[10px]'}`}
                      style={{
                        backgroundColor: isSurgery ? stage.mid : stage.soft,
                        color: isSurgery ? '#FFFFFF' : stage.accentDark,
                      }}
                      data-testid={`mock-b-proc-type-${i}`}>
                  {p.type}
                </span>
              )}
              {p.duration_minutes ? (
                <span className={`text-slate-600 font-medium flex items-center gap-1 ${isOpen ? 'text-sm' : 'text-[11px]'}`} data-testid={`mock-b-proc-duration-${i}`}>
                  <Clock size={isOpen ? 14 : 11} style={{ color: stage.accentDark }} />{p.duration_minutes} min
                </span>
              ) : null}
              {priceLabel && (
                <span className={`text-slate-800 font-semibold ml-auto ${isOpen ? 'text-base' : 'text-[12px]'}`} data-testid={`mock-b-proc-price-${i}`}>
                  {priceLabel}
                </span>
              )}
            </div>
            {!isOpen && (
              <p className="text-[10px] mt-2 font-semibold flex items-center gap-1" style={{ color: stage.accentDark }}>
                Tap to read more
                <ChevronRight size={11} />
              </p>
            )}
          </div>
          {isOpen && (
            <span className="shrink-0 ml-auto -mr-1 -mt-1 inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-slate-100 text-slate-500" data-testid={`mock-b-proc-close-${i}`}>
              <X size={18} />
            </span>
          )}
        </div>
      </button>
      {isOpen && (
        <div className="px-6 pb-7 pt-0 border-t" style={{ borderColor: stage.soft, backgroundColor: '#FCFCFD' }} data-testid={`mock-b-proc-expand-${i}`}>
          <div className={`grid gap-8 mt-5 ${indications.length > 0 ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
            {p.description && (
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] font-bold mb-3" style={{ color: stage.accentDark }}>What this involves</p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed text-justify hyphens-auto" data-testid={`mock-b-proc-full-desc-${i}`}>{p.description}</p>
              </div>
            )}
            {indications.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] font-bold mb-3" style={{ color: stage.accentDark }}>Common indications</p>
                <ul className="space-y-2.5">
                  {indications.slice(0, 10).map((ind, k) => (
                    <li key={k} className="text-base text-slate-700 leading-relaxed flex items-start gap-2.5">
                      <CheckCircle size={16} className="shrink-0 mt-1" style={{ color: stage.accentDark }} />
                      {ind}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="mt-7 grid grid-cols-3 gap-4 pt-5 border-t" style={{ borderColor: stage.soft }}>
            <SmallStat label="Type" value={p.type || '—'} stage={stage} />
            <SmallStat label="Duration" value={p.duration_minutes ? `${p.duration_minutes} min` : 'Varies'} stage={stage} />
            <SmallStat label="Fees" value={priceLabel || 'On request'} stage={stage} />
          </div>
          <p className="text-xs text-slate-500 mt-5 italic">
            Final cost may vary based on case complexity, lab tests, and consumables. Confirm with the clinic before booking.
          </p>
        </div>
      )}
    </div>
  );
}

function SmallStat({ label, value, stage }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: stage.accentDark }}>{label}</p>
      <p className="text-base font-semibold text-slate-900 mt-1">{value}</p>
    </div>
  );
}

function Section({ id, eyebrow, title, stage, children }) {
  return (
    <section id={`sec-${id}`} className="scroll-mt-32" data-testid={`mock-b-section-${id}`}>
      <div className="mb-7">
        <p className="text-sm md:text-base uppercase tracking-[0.22em] font-bold mb-2.5" style={{ color: stage.accentDark }}>{eyebrow}</p>
        <h2 className="mockB-h-base font-medium leading-[1.05] tracking-tight text-slate-900"
            style={{ fontSize: 'clamp(1.85rem, 4.5vw, 4rem)', letterSpacing: '-0.02em' }}>
          {title}
        </h2>
      </div>
      {children}
    </section>
  );
}

function Tile({ icon, title, body }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 hover:border-slate-400 hover:-translate-y-0.5 transition-all"
         data-testid={`mock-b-tile-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-11 h-11 rounded-lg bg-slate-50 flex items-center justify-center mb-3">{icon}</div>
      <p className="font-semibold text-slate-900 text-[17px]" style={{ fontFamily: 'Inter, sans-serif' }}>{title}</p>
      <p className="text-[15px] text-slate-600 mt-1.5 leading-[1.55]">{body}</p>
    </div>
  );
}

function CompactInstallQrCard({ slug, stage, firstName, doctorName, testId }) {
  const url = `${(typeof window !== 'undefined' ? window.location.origin : 'https://docsite.care')}/c/${slug}`;
  const [dataUrl, setDataUrl] = React.useState('');
  // Compute the doctor's initials (e.g. "CT" for "Dr. Chetna Tikoo") so
  // we can render a centered identity-badge inside the QR. errorCorrection
  // 'Q' tolerates ~25% obstruction, so a small ~22% overlay is safe.
  const initials = React.useMemo(() => {
    const cleaned = (doctorName || firstName || '').replace(/^Dr\.?\s*/i, '').trim();
    const parts = cleaned.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return 'DR';
  }, [doctorName, firstName]);
  React.useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, { width: 360, margin: 1, errorCorrectionLevel: 'H', color: { dark: '#0f172a', light: '#ffffff' } })
      .then(d => { if (!cancelled) setDataUrl(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [url]);
  return (
    <div className="rounded-2xl border-2 bg-white p-4 w-full flex items-center gap-4"
         style={{ borderColor: stage.mid }}
         data-testid={testId}>
      <div className="rounded-xl bg-white p-1.5 border border-slate-200 shrink-0 relative" style={{ width: 124, height: 124 }}>
        {dataUrl ? (
          <>
            <img src={dataUrl} alt={`Scan to install DocSite for Dr. ${firstName}`} className="w-full h-full object-contain" data-testid="mock-b-hero-qr-image" />
            {/* Centered identity badge — "Dr CT" — gives the printed/onscreen
                QR a visible link to the doctor so users recognise it
                instantly even before they scan. */}
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              aria-hidden="true"
            >
              <div
                className="rounded-full flex flex-col items-center justify-center font-bold leading-none shadow-md"
                style={{
                  width: 34,
                  height: 34,
                  backgroundColor: stage.accent,
                  color: '#ffffff',
                  border: '2px solid #ffffff',
                  fontFamily: 'Inter, sans-serif',
                }}
                data-testid="mock-b-hero-qr-initials"
              >
                <span style={{ fontSize: 7, opacity: 0.9, letterSpacing: '0.06em', marginBottom: 1 }}>DR</span>
                <span style={{ fontSize: 12, letterSpacing: '0.02em' }}>{initials}</span>
              </div>
            </div>
          </>
        ) : (
          <div className="w-full h-full bg-slate-100 rounded animate-pulse" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1.5" style={{ color: stage.accentDark }}>
          <Smartphone size={11} /> Patient · scan to install
        </p>
        <p className="font-semibold text-slate-900 text-[14px] mt-1.5 leading-snug" style={{ fontFamily: 'Inter, sans-serif' }}>
          Point your phone — install DocSite + connect to Dr. {firstName} + get your MyConnect Portal for future bookings.
        </p>
      </div>
    </div>
  );
}

// Compute the doctor's clinic hours per location from the schedules array.
// schedules: [{ location_id, day_of_week (0=Mon..6=Sun), start_time, end_time, is_active }]
function ClinicHours({ schedules, locationId }) {
  const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = String(t).split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hr12 = ((h % 12) || 12);
    return m ? `${hr12}:${String(m).padStart(2, '0')} ${period}` : `${hr12} ${period}`;
  };
  // Group by day for this location.
  const rowsByDay = {};
  for (const s of schedules || []) {
    if (!s.is_active) continue;
    if (locationId && s.location_id !== locationId) continue;
    const d = s.day_of_week;
    if (!rowsByDay[d]) rowsByDay[d] = [];
    rowsByDay[d].push(`${formatTime(s.start_time)} – ${formatTime(s.end_time)}`);
  }
  const activeDays = Object.keys(rowsByDay).map(Number).sort((a, b) => a - b);
  if (activeDays.length === 0) {
    return <p className="text-base text-slate-500 italic">Hours not yet published</p>;
  }
  return (
    <div className="space-y-1 text-base text-slate-700" data-testid="mock-b-clinic-hours">
      {activeDays.map(d => (
        <div key={d} className="flex items-baseline gap-2">
          <span className="font-semibold text-slate-900 w-10 shrink-0">{DAY_LABELS[d]}</span>
          <span className="text-slate-600">{rowsByDay[d].join(' · ')}</span>
        </div>
      ))}
      {[0,1,2,3,4,5,6].filter(d => !rowsByDay[d]).length > 0 && (
        <p className="text-sm text-slate-400 pt-1">Closed: {[0,1,2,3,4,5,6].filter(d => !rowsByDay[d]).map(d => DAY_LABELS[d]).join(', ')}</p>
      )}
    </div>
  );
}
const ROLE_LABELS = {
  consultant: 'Consultant',
  attached: 'Attached',
  visiting_surgeon: 'Visiting surgeon',
  in_house: 'In-house',
};

// Case study card — doctor-authored "in my own words" clinical narrative.
// Date pill shows month/year. Archived entries are subtly muted.
function CaseStudyCard({ cs, stage, firstName, archived = false }) {
  const date = cs.created_at ? new Date(cs.created_at) : null;
  const dateLabel = date && !isNaN(date.getTime())
    ? date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
    : null;
  return (
    <article className={`rounded-2xl border ${archived ? 'border-slate-200 bg-slate-50/50' : 'border-slate-200 bg-white'} p-5 md:p-7`}
             data-testid={`mock-b-case-study-${cs.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        {cs.title && (
          <h3 className="mockB-h-base font-medium leading-tight tracking-tight text-slate-900 flex-1 min-w-0"
              style={{ fontSize: 'clamp(1.1rem, 2.2vw, 1.85rem)' }}>
            {cs.title}
          </h3>
        )}
        <div className="flex items-center gap-2 shrink-0">
          {dateLabel && (
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: stage.soft, color: stage.accentDark }}>
              {dateLabel}
            </span>
          )}
          {archived && (
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-500">
              Archived
            </span>
          )}
        </div>
      </div>
      {cs.body && (
        <p className="text-slate-700 leading-[1.7] whitespace-pre-line"
           style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(1rem, 1.15vw, 1.13rem)' }}>
          {cs.body}
        </p>
      )}
      <p className="text-xs text-slate-500 mt-3 italic">
        — Dr. {firstName}, in my own words
      </p>
    </article>
  );
}

// Article card — links to /dr/{slug}/articles/{slug} where the full text +
// Article JSON-LD lives. Reading-time + date signals substantiveness.
function ArticleCard({ article, stage, doctorSlug }) {
  // Backend list endpoint returns: { id, title, slug, meta_description,
  // published_at, primary_specialty, keywords }. Compute a soft reading
  // estimate from meta_description length so the card always shows a
  // substantive signal.
  const summary = article.summary || article.meta_description || '';
  const category = article.category || article.primary_specialty || 'Health insight';
  const readingMin = article.reading_time_min || (summary ? Math.max(3, Math.round(summary.split(/\s+/).length / 60)) : null);
  return (
    <Link to={`/dr/${doctorSlug}/articles/${article.slug}`}
          className="block rounded-2xl border border-slate-200 bg-white p-5 hover:border-slate-400 hover:-translate-y-0.5 transition-all"
          data-testid={`mock-b-article-${article.slug}`}>
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1.5" style={{ color: stage.accentDark }}>
        {category}
      </p>
      <h4 className="mockB-h-base font-semibold text-slate-900 leading-snug mb-2"
          style={{ fontSize: 'clamp(1.05rem, 1.6vw, 1.45rem)', fontWeight: 600 }}>
        {article.title}
      </h4>
      {summary && (
        <p className="text-sm text-slate-600 leading-relaxed mb-3 line-clamp-3">
          {summary}
        </p>
      )}
      <p className="text-xs text-slate-500">
        {readingMin ? `${readingMin} min read` : ''}
        {article.published_at && (
          <>
            {readingMin ? ' · ' : ''}
            {new Date(article.published_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
          </>
        )}
      </p>
    </Link>
  );
}



function HospitalAttachmentCard({ attachment, doctor, stage }) {
  const role = ROLE_LABELS[attachment.role] || 'Consultant';
  const procMap = (doctor.procedures || []).reduce((acc, p) => { acc[p.id] = p; return acc; }, {});
  const procs = (attachment.procedure_ids || []).map(pid => procMap[pid]).filter(Boolean);
  const isDpD = attachment.source === 'DpD';

  return (
    <div className="rounded-2xl border bg-white p-5 hover:border-slate-400 transition-colors"
         style={{ borderColor: isDpD ? '#e2e8f0' : stage.mid }}
         data-testid={`mock-b-hospital-${attachment.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
               style={{ backgroundColor: stage.soft }}>
            <Hospital size={18} style={{ color: stage.accentDark }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-slate-900 text-[17px] leading-tight"
                  style={{ fontFamily: 'Inter, sans-serif' }}>
                {attachment.hospital_name}
              </h4>
              {isDpD && (
                <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-bold tracking-wide bg-slate-100 text-slate-600 border border-slate-300"
                      title="Doctor-Provided Data — pending DocSite verification"
                      data-testid="mock-b-dpd-badge">
                  DpD
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-0.5">{attachment.city} · {role}</p>
          </div>
        </div>
      </div>

      {(attachment.accreditation || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="mock-b-hospital-accreditation">
          {attachment.accreditation.map(a => (
            <span key={a}
                  className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider"
                  style={{ backgroundColor: stage.soft, color: stage.accentDark, border: `1px solid ${stage.mid}` }}>
              <BadgeCheck size={11} /> {a}
            </span>
          ))}
        </div>
      )}

      {procs.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
            Procedures performed here
          </p>
          <div className="flex flex-wrap gap-1.5">
            {procs.slice(0, 6).map(p => (
              <span key={p.id}
                    className="inline-flex items-center rounded-full px-2.5 py-1 text-xs text-slate-700 border border-slate-200 bg-slate-50"
                    data-testid={`mock-b-hospital-proc-${p.id}`}>
                {p.name}
              </span>
            ))}
            {procs.length > 6 && (
              <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs text-slate-500 border border-dashed border-slate-300">
                +{procs.length - 6} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}



function RatingCol({ label, score, stage }) {
  return (
    <div data-testid={`mock-b-rating-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-sm font-semibold text-slate-900">{label}</p>
      <div className="flex items-center gap-1 mt-1.5">
        {[1,2,3,4,5].map(s => (
          <Star key={s} size={14} className={s <= Math.round(score) ? 'text-amber-400' : 'text-slate-300'} fill={s <= Math.round(score) ? 'currentColor' : 'none'} />
        ))}
        <span className="ml-1.5 text-base font-bold tabular-nums" style={{ color: stage.accentDark }}>{score}</span>
      </div>
    </div>
  );
}

// ============== TQS — DocSite's authentic rating engine ==============
//
// Replaces feeling-based stars with an objective 0-100 Treatment Quality
// Score computed from a 4-question yes/no survey sent automatically AFTER a
// verified consultation. The doctor cannot influence the score. We surface
// every layer of provenance: composite ring, 4 metric bars, methodology
// strip, individual notes with per-survey ✓/✗ dots, and a credible empty
// state.
function TqsBlock({ slug, stage, firstName }) {
  const [agg, setAgg] = React.useState(null);
  const [notes, setNotes] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get(`/public/doctors/${slug}/tqs`).then(r => r.data).catch(() => null),
      api.get(`/public/doctors/${slug}/tqs/notes?limit=10`).then(r => r.data).catch(() => null),
    ]).then(([a, n]) => {
      if (cancelled) return;
      setAgg(a); setNotes(n);
    });
    return () => { cancelled = true; };
  }, [slug]);

  if (!agg) return <div className="text-sm text-slate-400 py-6">Loading quality score…</div>;

  const minRequired = agg.min_required || 5;
  const ready = agg.count >= minRequired;
  const score = Math.round(agg.avg_score || 0);

  return (
    <div className="space-y-6" data-testid="mock-b-tqs-block">
      {/* Methodology strip — front and centre, not buried */}
      <div className="rounded-xl border p-4 flex items-start gap-3" style={{ backgroundColor: stage.soft, borderColor: stage.mid }} data-testid="mock-b-tqs-methodology">
        <Shield size={18} style={{ color: stage.accentDark }} className="shrink-0 mt-0.5" />
        <div className="text-base text-slate-800 leading-relaxed">
          <strong>DocSite scores aren't opinions — they're computed.</strong> Every rating below comes from a 4-question yes/no survey sent automatically <em>7 days after</em> a verified consultation. Doctors cannot pay to alter, remove, or boost results.{' '}
          <Link to="/methodology/tqs" className="font-semibold hover:underline" style={{ color: stage.accentDark }}>How TQS works →</Link>
        </div>
      </div>

      {ready ? (
        <>
          {/* Composite TQS ring + n */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center bg-white rounded-2xl border border-slate-200 p-6">
            <div className="md:col-span-4 flex justify-center">
              <TqsRing score={score} stage={stage} />
            </div>
            <div className="md:col-span-8">
              <p className="text-xs uppercase tracking-[0.22em] font-bold mb-1.5" style={{ color: stage.accentDark }}>Treatment Quality Score</p>
              <h3 className="mockB-h-base font-medium text-slate-900 leading-[1.1]" style={{ fontSize: 'clamp(1.15rem, 2.4vw, 2.2rem)' }}>
                Computed from {agg.count} verified post-consultation {agg.count === 1 ? 'survey' : 'surveys'}.
              </h3>
              <p className="text-base text-slate-600 mt-2">
                Higher score = patients consistently said "yes" to: heard fully, examined &amp; explained, clear next steps, follow-through.
              </p>
              <p className="text-[11px] text-slate-400 mt-2 font-medium">Last computed {agg.last_updated ? new Date(agg.last_updated).toLocaleDateString() : 'recently'}</p>
            </div>
          </div>

          {/* 4 metric bars — bars not stars */}
          <div className="grid sm:grid-cols-2 gap-3">
            <TqsBar label="Heard your symptoms fully" pct={agg.q1_yes_pct ?? 0} n={agg.count} stage={stage} weight={25} />
            <TqsBar label="Examined & explained the issue" pct={agg.q2_yes_pct ?? 0} n={agg.count} stage={stage} weight={25} />
            <TqsBar label="Clear next steps & success rate" pct={agg.q3_yes_pct ?? 0} n={agg.count} stage={stage} weight={20} />
            <TqsBar label="Treatment plan worked as explained" pct={agg.q4_yes_pct ?? 0} n={agg.count} stage={stage} weight={30} />
          </div>
        </>
      ) : (
        <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center" data-testid="mock-b-tqs-empty">
          <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: stage.soft }}>
            <Sparkles size={22} style={{ color: stage.accentDark }} />
          </div>
          <p className="font-semibold text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
            Treatment Quality Score is being established
          </p>
          <p className="text-base text-slate-600 mt-2 leading-relaxed max-w-md mx-auto">
            Dr. {firstName}'s TQS will appear once <strong>{minRequired}</strong> verified post-consultation surveys are completed. So far: <strong>{agg.count} of {minRequired}</strong>. We don't show fake stars to fill space.
          </p>
        </div>
      )}

      {/* Verified patient notes — qualitative comments, no stars */}
      {notes && notes.notes.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500 mt-2">Verified patient notes</p>
          {notes.notes.map((n, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`mock-b-tqs-note-${i}`}>
              {n.note && (
                <p className="text-slate-800 leading-[1.55] text-[17px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                  &ldquo;{n.note}&rdquo;
                </p>
              )}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3">
                <TqsDot ok={n.q1} label="Heard" late={n.freshness_sameday !== undefined && n.freshness_sameday < 1.0} />
                <TqsDot ok={n.q2} label="Examined" late={n.freshness_sameday !== undefined && n.freshness_sameday < 1.0} />
                <TqsDot ok={n.q3} label="Next steps" late={n.freshness_sameday !== undefined && n.freshness_sameday < 1.0} />
                <TqsDot ok={n.q4} label="Plan worked" late={n.freshness_outcome !== undefined && n.freshness_outcome < 1.0} />
                {(n.freshness_sameday < 1.0 || n.freshness_outcome < 1.0) && (
                  <span title={`Late submission · weighted at ${Math.round(((n.freshness_sameday || 1) + (n.freshness_outcome || 1)) / 2 * 100)}%`}
                        className="inline-flex items-center gap-1 text-[10px] text-slate-400 font-semibold">
                    <Clock size={10} /> late
                  </span>
                )}
                <span className="text-[11px] text-slate-400 ml-auto font-medium">
                  {n.patient || 'Verified patient'} · {n.completed_at ? new Date(n.completed_at).toLocaleDateString() : '—'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TqsRing({ score, stage }) {
  // Colour band: <60 amber, 60-79 stage, 80+ emerald
  const ringColor = score >= 80 ? '#10B981' : score >= 60 ? stage.accent : '#F59E0B';
  const dash = (score / 100) * 282.6; // 2πr where r=45
  return (
    <div className="relative w-44 h-44" data-testid="mock-b-tqs-ring">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r="45" fill="none" stroke="#E5E7EB" strokeWidth="6" />
        <circle cx="50" cy="50" r="45" fill="none" stroke={ringColor} strokeWidth="6"
                strokeLinecap="round" strokeDasharray={`${dash} 282.6`} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-5xl font-bold tabular-nums text-slate-900" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>{score}</span>
        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mt-0.5">/ 100 · TQS</span>
      </div>
    </div>
  );
}

function TqsBar({ label, pct, n, stage, weight }) {
  const fillColor = pct >= 80 ? '#10B981' : pct >= 60 ? stage.accent : '#F59E0B';
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`mock-b-tqs-bar-${label.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}>
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <p className="text-base font-semibold text-slate-900 leading-tight" style={{ fontFamily: 'Inter, sans-serif' }}>{label}</p>
        <span className="text-xl font-bold tabular-nums" style={{ color: fillColor }}>{Math.round(pct)}<span className="text-xs">%</span></span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: fillColor }} />
      </div>
      <p className="text-[11px] text-slate-400 mt-1.5 font-medium">n = {n} · weight {weight}%</p>
    </div>
  );
}

function TqsDot({ ok, label, late }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold">
      <span className={`w-2 h-2 rounded-full ${ok ? 'bg-emerald-500' : 'bg-rose-500'} ${late ? 'opacity-50' : ''}`} />
      <span className={`${ok ? 'text-slate-700' : 'text-slate-400 line-through'} ${late ? 'opacity-70' : ''}`}>{label}</span>
    </span>
  );
}

function BookingHeroCard({ doctor, stage, primarySpec, firstName, photoSrc, cc, slug, bookingMode, setBookingMode, onPickDate, onPickSlot, onShowToast, onSeeAll, onCall, onChat }) {
  // Real availability — fetch next 3 days, show 2 slot peeks per day.
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    const fetchDays = async () => {
      // Anchor to LOCAL midnight so we never accidentally render yesterday
      // when the browser clock or cache is slightly skewed. Always show
      // Today + next 2 days, regardless of timezone offset.
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const targets = [0, 1, 2].map(off => {
        const d = new Date(today); d.setDate(today.getDate() + off); return d;
      });
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const labels = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
      try {
        const results = await Promise.all(targets.map(async (d) => {
          try {
            const r = await api.get(`/doctor/${slug}/availability`, { params: { date: fmt(d) } });
            const allSlots = (r.data || []).flatMap(loc => (loc.slots || []).filter(s => s.is_available).map(s => s.start_time));
            return {
              dateObj: d,
              day: labels[d.getDay() === 0 ? 6 : d.getDay() - 1],
              date: d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
              slots: allSlots.slice(0, 4),
              total: allSlots.length,
            };
          } catch { return { dateObj: d, day: labels[d.getDay() === 0 ? 6 : d.getDay() - 1], date: d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }), slots: [], total: 0 }; }
        }));
        if (!cancelled) setDays(results);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDays();
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div className="rounded-2xl bg-white border-2 shadow-xl overflow-hidden" style={{ borderColor: stage.accentDark }} data-testid="mock-b-booking-hero">
      {/* Doctor mini header */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-200">
        {photoSrc ? (
          <img src={photoSrc} alt={doctor.name} className="w-16 h-20 rounded-xl object-cover border-2 shadow-sm" style={{ borderColor: stage.mid }} />
        ) : (
          <div className="w-16 h-20 rounded-xl overflow-hidden border-2 shadow-sm flex items-center justify-center" style={{ borderColor: stage.mid, backgroundColor: stage.soft }}>
            <div className="w-12 h-12">
              {(() => { const m = computeMonogram(doctor.name, doctor.specializations); return <MonogramAvatar initials={m.initials} bgColor={m.bg_color} size={48} />; })()}
            </div>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 truncate text-[17px]" style={{ fontFamily: 'Inter, sans-serif' }}>Dr. {doctor.name?.replace(/^Dr\.?\s*/i, '')}</p>
          <p className="text-sm text-slate-500 truncate font-medium">{primarySpec} · {doctor.experience_years || 0} yrs</p>
          {doctor.rating?.score && (
            <p className="text-sm font-semibold text-amber-600 flex items-center gap-1 mt-0.5">
              <Star size={11} fill="currentColor" /> {doctor.rating.score}/5
            </p>
          )}
        </div>
      </div>

      {/* Title + CTA */}
      <div className="p-5">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold mb-1.5" style={{ color: stage.accentDark }}>
          Direct booking · No middlemen
        </p>
        <h3 className="mockB-h-base font-medium text-slate-900 leading-[1.1]"
            style={{ fontSize: 'clamp(1.2rem, 2vw, 1.85rem)' }}>
          Book an appointment with Dr. {firstName}
        </h3>
        <p className="text-sm text-slate-500 mt-2">No platform fee. Slots confirm instantly.</p>

        {/* Visit type toggle — In-person works; Video shows a "coming soon" toast */}
        <div className="grid grid-cols-2 gap-2 mt-4" data-testid="mock-b-mode-toggle">
          <button onClick={() => setBookingMode('in_person')}
                  className="rounded-lg px-3 py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 border-2 transition-all"
                  style={bookingMode === 'in_person'
                    ? { borderColor: stage.accent, backgroundColor: stage.soft, color: stage.accentDark }
                    : { borderColor: 'rgb(203, 213, 225)', backgroundColor: 'white', color: 'rgb(71, 85, 105)' }}
                  data-testid="mock-b-mode-in-person">
            <Calendar size={14} /> In-person
          </button>
          <button onClick={() => { setBookingMode('video'); onShowToast && onShowToast('Video consultations are coming soon. For now, please book an in-person visit.'); setTimeout(() => onShowToast && onShowToast(''), 4500); }}
                  className="rounded-lg px-3 py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 border-2 transition-all"
                  style={bookingMode === 'video'
                    ? { borderColor: stage.accent, backgroundColor: stage.soft, color: stage.accentDark }
                    : { borderColor: 'rgb(203, 213, 225)', backgroundColor: 'white', color: 'rgb(100, 116, 139)' }}
                  data-testid="mock-b-mode-video">
            Video visit
          </button>
        </div>

        {/* 3-day slot peek — REAL availability */}
        <div className="mt-4 grid grid-cols-3 gap-1.5">
          {loading && [0,1,2].map(i => (
            <div key={i} className="rounded-lg border border-slate-200 p-2 text-center animate-pulse">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-300">···</p>
              <div className="h-3 bg-slate-100 rounded mt-1"></div>
              <div className="h-5 bg-slate-100 rounded mt-2"></div>
            </div>
          ))}
          {!loading && days.map((d, i) => (
            <button key={d.dateObj.toISOString()} onClick={() => onPickDate && onPickDate(d.dateObj)}
                 className="rounded-lg border border-slate-200 p-2 text-center hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`mock-b-slot-day-${i}`}>
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{d.day}</p>
              <p className="text-xs font-semibold text-slate-900 mt-0.5">{d.date}</p>
              <div className="mt-2 space-y-1">
                {d.slots.length === 0 ? (
                  <p className="text-[10px] text-slate-400 font-medium py-1">Closed</p>
                ) : (
                  d.slots.slice(0, 2).map(s => (
                    <span key={s} onClick={(e) => { e.stopPropagation(); onPickSlot && onPickSlot(d.dateObj); }}
                            className="block w-full rounded text-[11px] font-semibold py-1 hover:scale-105 transition-transform cursor-pointer"
                            style={{ backgroundColor: stage.soft, color: stage.accentDark }}
                            data-testid={`mock-b-slot-${i}-${s.replace(/[^0-9a-z]/gi,'')}`}>{s}</span>
                  ))
                )}
                {d.total > 2 && (
                  <p className="text-[10px] font-medium text-slate-400">+{d.total - 2} more</p>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Big stage CTA */}
        <button onClick={onSeeAll}
                className="mt-4 w-full rounded-full py-3.5 text-base font-bold text-white transition-transform hover:scale-[1.02] flex items-center justify-center gap-2"
                style={{ backgroundColor: stage.accent }}
                data-testid="mock-b-hero-book-btn">
          See all available slots <ArrowRight size={16} />
        </button>

        {/* Contact action row — Turbo-gated Message + Call, plus optional WhatsApp */}
        <ContactRow stage={stage} doctor={doctor} firstName={firstName} cc={cc} onCall={onCall} onChat={onChat} />

        {doctor.consultation_fee && (
          <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between">
            <span className="text-sm text-slate-500 uppercase tracking-widest font-bold">Consult fee</span>
            <span className="font-bold text-slate-900 text-base">Rs. {doctor.consultation_fee}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function BookingSecondaryCard({ stage, firstName, doctor, cc, onCall, onChat }) {
  // In-app chat is now ALWAYS the primary action (no tier gate). Patients get
  // a real private thread; doctors reply from their dashboard. WhatsApp/call
  // stay as gated secondary options surfaced by the doctor's settings.
  const showCall = cc?.calls_enabled;
  const showWA = cc?.whatsapp_personal_enabled;
  const primaryLabel = `Message Dr. ${firstName}`;
  const onPrimary = () => onChat();
  const locked = false;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <p className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1.5" style={{ color: stage.accentDark }}>
        Quick book
      </p>
      <h4 className="font-semibold text-slate-900 text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
        Need an appointment fast?
      </h4>
      <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
        Skip the form — message Dr. {firstName} directly. Replies typically arrive within 1 hour during clinic hours.
      </p>
      <button onClick={onPrimary}
         className={`mt-4 inline-flex items-center justify-center gap-1.5 w-full rounded-full py-3 text-xs sm:text-sm font-bold text-white transition-all ${locked ? 'opacity-90' : 'hover:scale-[1.02]'}`}
         style={{ backgroundColor: stage.accent }}
         data-testid="mock-b-quickbook-btn">
        <MessageCircle size={14} /> <span className="truncate">{primaryLabel}</span>
      </button>

      {/* Trust strip — fills the card with high-signal patient facts so the
          card stops feeling half-empty when the doctor's schedule is sparse. */}
      <QuickBookTrustStrip doctor={doctor} stage={stage} />
    </div>
  );
}

// High-signal trust facts displayed below the Quick Book CTA — fills vertical
// real-estate with patient-decision-helpful content (reply time, languages,
// payment methods, hospital affiliations). Every visible row is sourced from
// the doctor's profile data so a doctor who fills their profile gets a denser
// card automatically.
function QuickBookTrustStrip({ doctor, stage }) {
  const languages = (doctor.languages_spoken || doctor.languages || []).slice(0, 4);
  const payments = doctor.payment_methods || ['Cash', 'UPI', 'Card'];
  const hospitals = (doctor.hospital_affiliations || []).slice(0, 3);
  const replyWindow = doctor.calls_chat_settings?.reply_window_label || 'Within 1 hour during clinic hours';

  const rows = [
    { icon: Clock, label: 'Typical reply', value: replyWindow },
    languages.length && { icon: Globe, label: 'Languages', value: languages.join(' · ') },
    payments.length && { icon: CreditCard, label: 'Payments', value: payments.join(' · ') },
    hospitals.length && { icon: Building2, label: 'Also seen at', value: hospitals.join(' · ') },
  ].filter(Boolean);

  return (
    <div className="mt-5 pt-4 border-t border-slate-100 space-y-3" data-testid="mock-b-quickbook-trust-strip">
      {rows.map((r, i) => (
        <div key={i} className="flex items-start gap-2.5 text-xs">
          <r.icon size={13} className="mt-0.5 shrink-0" style={{ color: stage.accentDark }} />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{r.label}</p>
            <p className="text-slate-700 leading-snug">{r.value}</p>
          </div>
        </div>
      ))}
      <p className="text-[11px] text-slate-400 leading-relaxed pt-1">
        Verified DocSite doctor · NMC-registered · Encrypted messaging
      </p>
    </div>
  );
}

// Turbo lock badge
function TurboTag() {
  return (
    <span className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-amber-800 font-bold text-[9px] tracking-wider uppercase" data-testid="mock-b-turbo-tag">
      <Lock size={9} /> Turbo
    </span>
  );
}

// Contact action row inside the booking hero card. ALWAYS shows two
// buttons (Message Dr. + Call Clinic), each Turbo-lockable. WhatsApp only
// renders as a third row when the doctor has explicitly opted in to expose
// their personal wa.me link (privacy-first default).
function ContactRow({ stage, doctor, firstName, cc, onCall, onChat }) {
  const callLabel = cc.calls_enabled
    ? (cc.call_route === 'receptionist'
        ? `Call clinic${cc.receptionist_name ? ` · ${cc.receptionist_name}` : ''}`
        : `Call Dr. ${firstName}`)
    : `Call clinic`;
  const Btn = ({ icon, label, onClick, locked, dataTestid }) => (
    <button onClick={onClick}
            className={`relative rounded-full py-2.5 text-xs sm:text-sm font-semibold border flex items-center justify-center gap-1.5 transition-colors ${locked ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100' : 'border-slate-300 text-slate-700 hover:border-slate-500'}`}
            data-testid={dataTestid}>
      {icon} <span className="truncate">{label}</span>
      {locked && <Lock size={11} className="text-amber-600 ml-0.5 flex-shrink-0" />}
    </button>
  );
  return (
    <div className="space-y-2 mt-3">
      <div className="grid grid-cols-2 gap-2">
        <Btn icon={<MessageCircle size={14} />}
             label={cc.chat_enabled ? 'Message Dr.' : 'Message Dr.'}
             onClick={onChat}
             locked={!cc.chat_enabled}
             dataTestid="mock-b-hero-chat-btn" />
        <Btn icon={<Phone size={14} />}
             label={cc.calls_enabled ? callLabel : 'Call clinic'}
             onClick={onCall}
             locked={!cc.calls_enabled}
             dataTestid="mock-b-hero-call-btn" />
      </div>
      {cc.whatsapp_personal_enabled && (
        <a href={`https://wa.me/${(doctor.phone || '').replace(/\D/g, '') || '910000000000'}`}
           className="block rounded-full py-3 text-sm font-semibold text-center text-emerald-700 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100"
           data-testid="mock-b-hero-whatsapp">
          <MessageCircle size={14} className="inline mr-1.5" /> WhatsApp Dr. (personal)
        </a>
      )}
    </div>
  );
}

// ============== Modals ==============

function ModalShell({ children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm" data-testid={testId} onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-7 shadow-2xl animate-in zoom-in-95 fade-in duration-200" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function CallModal({ modal, stage, firstName, doctor, onProceed, onClose }) {
  if (modal.state === 'locked') {
    return <TurboLockedModal stage={stage} firstName={firstName} title="Voice calling is a Turbo feature" body={`Dr. ${firstName} hasn't activated direct calling yet. With Turbo, patients tap once to call without ever seeing a phone number.`} onClose={onClose} />;
  }
  if (modal.state === 'advisory') {
    return (
      <ModalShell onClose={onClose} testId="mock-b-call-advisory-modal">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: stage.soft }}>
            <Phone size={18} style={{ color: stage.accentDark }} />
          </div>
          <h3 className="font-semibold text-slate-900 text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>Switch to mobile data first</h3>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed mt-2">
          Voice calls work best on <strong>mobile internet (4G/5G)</strong>. Many WiFi networks — corporate, hotels, public — block voice traffic and the call will silently fail.
        </p>
        <p className="text-xs text-slate-500 mt-3">Connection detected: <span className="font-mono">{modal.networkType}</span></p>
        <div className="grid grid-cols-2 gap-2 mt-5">
          <button onClick={onClose}
                  className="rounded-full py-2.5 px-4 text-sm font-semibold border border-slate-300 text-slate-700 hover:border-slate-500"
                  data-testid="mock-b-call-cancel-btn">
            I'll switch first
          </button>
          <button onClick={onProceed}
                  className="rounded-full py-2.5 px-4 text-sm font-semibold text-white"
                  style={{ backgroundColor: stage.accent }}
                  data-testid="mock-b-call-continue-btn">
            Continue on this network
          </button>
        </div>
      </ModalShell>
    );
  }
  if (modal.state === 'connecting') {
    return (
      <ModalShell onClose={onClose} testId="mock-b-call-connecting-modal">
        <div className="text-center py-3">
          <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center animate-pulse" style={{ backgroundColor: stage.soft }}>
            <Phone size={26} style={{ color: stage.accentDark }} />
          </div>
          <h3 className="font-semibold text-slate-900 text-lg mt-4">Calling Dr. {firstName}…</h3>
          <p className="text-sm text-slate-500 mt-2">{modal.route === 'receptionist' ? 'Routing to clinic reception (app-to-app)' : 'Direct (app-to-app, no number exposed)'}</p>
          <p className="text-xs text-amber-600 mt-3 font-semibold">Beta · VoIP infrastructure rolling out — see fallback in a few seconds</p>
        </div>
      </ModalShell>
    );
  }
  if (modal.state === 'failed') {
    return (
      <ModalShell onClose={onClose} testId="mock-b-call-fallback-modal">
        <h3 className="font-semibold text-slate-900 text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>Couldn't connect the call</h3>
        <p className="text-sm text-slate-600 leading-relaxed mt-2">
          The VoIP layer is in beta — sometimes blocked by strict networks. Try a different network or use one of the alternatives below to reach Dr. {firstName} now.
        </p>
        <div className="grid gap-2 mt-5">
          {doctor.calls_chat?.whatsapp_personal_enabled && (
            <a href={`https://wa.me/${(doctor.phone || '').replace(/\D/g, '') || '910000000000'}`}
               className="rounded-full py-2.5 text-sm font-bold text-white text-center"
               style={{ backgroundColor: '#25D366' }}>
              Open WhatsApp instead
            </a>
          )}
          <button onClick={onClose} className="rounded-full py-2.5 text-sm font-semibold border border-slate-300 text-slate-700 hover:border-slate-500">
            Close
          </button>
        </div>
      </ModalShell>
    );
  }
  return null;
}

function ChatModal({ modal, stage, firstName, slug, onClose }) {
  if (modal.state === 'locked') {
    return <TurboLockedModal stage={stage} firstName={firstName}
                             title="Private messaging is a Turbo feature"
                             body={`Dr. ${firstName} hasn't activated DocSite In-App Chat yet. With Turbo, you can message back and forth privately — no phone number exchanged.`}
                             onClose={onClose} />;
  }
  return <PatientChatPanel stage={stage} firstName={firstName} slug={slug} onClose={onClose} />;
}

function PatientChatPanel({ stage, firstName, slug, onClose }) {
  const STORAGE_KEY = `docsite_chat_${slug}`;
  // Authenticated patient? Pre-fill their name + mobile so they skip the
  // form and jump straight to the OTP/known-patient gate with one tap.
  // We don't auto-submit — the patient should still see/confirm what's
  // about to be sent (transparency > one fewer tap).
  const { user: authUser } = useAuth();
  const authPhoneDigits = ((authUser?.phone) || '').replace(/\D/g, '').slice(-10);
  const authIsPatient = authUser?.role === 'patient';
  const [phoneToken, setPhoneToken] = React.useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || ''; } catch { return ''; }
  });
  // Multi-step gate before first message:
  //   'role'    → Patient or Patient's Relative + name + mobile
  //   'wall'    → Anti-abuse: this phone already has a chat with this doctor →
  //               force log-in or patient profile registration
  //   'otp'     → enter code from SMS/email (skipped if precheck flagged known patient)
  //   'whose'   → after OTP, confirm whose mobile this is (patient/relative)
  //   'chat'    → free messaging surface
  const [step, setStep] = React.useState(phoneToken ? 'chat' : 'role');
  const [wallDoctorName, setWallDoctorName] = React.useState('');
  const [limitInfo, setLimitInfo] = React.useState(null);

  // Role + identity state — pre-filled from logged-in patient identity
  // when available, so logged-in patients see their info already in the form.
  const [role, setRole] = React.useState('patient');
  const [name, setName] = React.useState(authIsPatient ? (authUser?.name || '') : '');
  // Structured name parts. When a logged-in patient's stored name is one
  // word we treat it as `firstName`; for 2+ words we split conservatively
  // (first / middle / surname).
  const _initialNameParts = React.useMemo(() => {
    const raw = (authIsPatient ? (authUser?.name || '') : '').trim();
    if (!raw) return { first: '', middle: '', surname: '' };
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return { first: parts[0], middle: '', surname: '' };
    if (parts.length === 2) return { first: parts[0], middle: '', surname: parts[1] };
    return { first: parts[0], middle: parts.slice(1, -1).join(' '), surname: parts[parts.length - 1] };
  }, [authIsPatient, authUser]);
  const [patientFirst, setPatientFirst] = React.useState(_initialNameParts.first);
  const [patientMiddle, setPatientMiddle] = React.useState(_initialNameParts.middle);
  const [patientSurname, setPatientSurname] = React.useState(_initialNameParts.surname);
  const [phone, setPhone] = React.useState(authIsPatient ? authPhoneDigits : '');
  const [emailFallback, setEmailFallback] = React.useState('');
  const [showEmailField, setShowEmailField] = React.useState(false);

  // OTP state
  const [otpId, setOtpId] = React.useState('');
  const [otpChannel, setOtpChannel] = React.useState('');
  const [otpTarget, setOtpTarget] = React.useState('');
  const [otpCode, setOtpCode] = React.useState('');
  // Backend tells us exactly how many digits to expect — 4 for MSG91 SMS
  // (configurable via MSG91_OTP_LENGTH env), 6 for the email fallback.
  // Defaults to 4 before the send-otp response arrives.
  const [otpLength, setOtpLength] = React.useState(4);
  const [chatToken, setChatToken] = React.useState('');

  // Post-OTP: whose mobile?
  const [mobileIs, setMobileIs] = React.useState('patient');
  const [patientPhone, setPatientPhone] = React.useState('');

  // Chat state
  const [text, setText] = React.useState('');
  const [messages, setMessages] = React.useState([]);
  const [sending, setSending] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [loaded, setLoaded] = React.useState(false);
  const scrollRef = React.useRef(null);

  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const refresh = React.useCallback(async () => {
    if (!phoneToken) { setLoaded(true); return; }
    try {
      const r = await fetch(`${apiBase}/api/public/chat/${slug}/thread?phone_token=${encodeURIComponent(phoneToken)}`);
      if (r.ok) {
        const data = await r.json();
        setMessages(data.messages || []);
        if (data.patient_name) setName(data.patient_name);
      } else if (r.status === 404) {
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* private mode */ }
        setPhoneToken('');
        setStep('role');
      }
    } catch { /* network blip — silent */ }
    setLoaded(true);
  }, [phoneToken, slug, apiBase, STORAGE_KEY]);

  React.useEffect(() => { refresh(); }, [refresh]);

  // Poll every 5s while chat is open for doctor replies.
  React.useEffect(() => {
    if (!phoneToken || step !== 'chat') return;
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [phoneToken, step, refresh]);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, step]);

  // Web-push subscription for anonymous patients. Fires once per chat
  // session: when the patient lands in the chat step with a valid
  // phone_token AND hasn't been asked yet AND the browser supports push.
  // The native permission prompt only appears on the FIRST chat session;
  // a previous "denied" stays sticky in the browser. We use sessionStorage
  // so a single session never re-prompts.
  React.useEffect(() => {
    if (step !== 'chat' || !phoneToken) return;
    const VAPID = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!VAPID || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
    const SESSION_FLAG = `docsite_chat_push_asked_${slug}`;
    try { if (sessionStorage.getItem(SESSION_FLAG)) return; } catch { /* private mode */ }
    try { sessionStorage.setItem(SESSION_FLAG, '1'); } catch { /* private mode */ }

    (async () => {
      try {
        // If the user already denied notifications globally, do nothing.
        if (Notification.permission === 'denied') return;
        // Give it a beat — don't slam the patient with a perm dialog the
        // very moment chat opens. ~2.5 s feels organic.
        await new Promise(r => setTimeout(r, 2500));
        const perm = Notification.permission === 'granted'
          ? 'granted'
          : await Notification.requestPermission();
        if (perm !== 'granted') return;

        const reg = await navigator.serviceWorker.ready;
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          // Convert URL-safe base64 VAPID key → Uint8Array
          const pad = '='.repeat((4 - VAPID.length % 4) % 4);
          const b64 = (VAPID + pad).replace(/-/g, '+').replace(/_/g, '/');
          const raw = window.atob(b64);
          const arr = new Uint8Array(raw.length);
          for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
          sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: arr });
        }
        await fetch(`${apiBase}/api/public/chat-push/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone_token: phoneToken, doctor_slug: slug, subscription: sub.toJSON() }),
        });
      } catch { /* user closed prompt, network blip — silent */ }
    })();
  }, [step, phoneToken, slug, apiBase]);

  // ── STEP 1 → 2: PRECHECK + (auto-skip OTP if known patient) ─────────────
  const submitRole = async () => {
    setErr('');
    const trimFirst = (patientFirst || '').trim();
    const trimMid = (patientMiddle || '').trim();
    const trimSur = (patientSurname || '').trim();
    if (trimFirst.length < 1) { setErr('Please enter your first name.'); return; }
    const composedFull = [trimFirst, trimMid, trimSur].filter(Boolean).join(' ');
    setName(composedFull);
    const cleaned = (phone || '').replace(/\D/g, '');
    if (cleaned.length < 10) { setErr('Enter a valid 10-digit mobile.'); return; }
    setBusy(true);
    try {
      // Precheck — server tells us if this mobile is a known patient.
      const r1 = await fetch(`${apiBase}/api/public/chat/${slug}/precheck`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role,
          patient_name: composedFull,
          first_name: trimFirst,
          middle_name: trimMid,
          surname: trimSur,
          phone: cleaned,
        }),
      });
      const d1 = await r1.json().catch(() => ({}));
      if (!r1.ok) throw new Error(d1.detail || 'Could not start chat');

      // Anti-abuse wall — phone already has a chat with this doctor and
      // visitor isn't logged in. Force log-in or registration.
      if (d1.requires_login) {
        setWallDoctorName(d1.doctor_name || `Dr. ${firstName}`);
        setStep('wall');
        return;
      }

      // Plan-limit reached — logged-in patient has used up their chat sessions
      // for this doctor in the current window.
      if (d1.limit_reached) {
        setWallDoctorName(d1.doctor_name || `Dr. ${firstName}`);
        setLimitInfo({
          used: d1.sessions_used,
          cap: d1.sessions_cap,
          window_days: d1.window_days,
          source: d1.policy_source,
        });
        setStep('limit');
        return;
      }

      if (!d1.otp_required && d1.chat_token) {
        // Known patient — skip OTP. Default mobile-is = role they chose.
        setChatToken(d1.chat_token);
        setMobileIs(role === 'relative' ? 'relative' : 'patient');
        setStep('whose');
        return;
      }

      // Unknown — send OTP.
      const body = { phone: cleaned };
      if (emailFallback.trim()) body.email = emailFallback.trim();
      const r2 = await fetch(`${apiBase}/api/public/chat/${slug}/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d2 = await r2.json().catch(() => ({}));
      if (!r2.ok) {
        // If SMS failed and no email provided, reveal the email fallback field.
        if (r2.status === 502) { setShowEmailField(true); throw new Error(d2.detail || 'SMS failed — add email to retry.'); }
        throw new Error(d2.detail || 'Could not send code');
      }
      setOtpId(d2.otp_id);
      setOtpChannel(d2.channel);
      setOtpTarget(d2.masked_target || '');
      setOtpLength(typeof d2.otp_length === 'number' ? d2.otp_length : 4);
      setOtpCode('');
      setStep('otp');
    } catch (e) {
      setErr(e.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  // ── STEP 2: VERIFY OTP ──────────────────────────────────────────────────
  const submitOtp = async () => {
    setErr('');
    const code = (otpCode || '').replace(/\D/g, '');
    // MSG91 widget length is configurable in the MSG91 dashboard (typically
    // 4 or 6 digits); our email-channel fallback always sends 6 digits.
    // Accept 4-6 digits and let the verify endpoint reject if wrong.
    if (code.length !== otpLength) { setErr(`Enter the ${otpLength}-digit code we sent.`); return; }
    setBusy(true);
    try {
      const r = await fetch(`${apiBase}/api/public/chat/${slug}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otp_id: otpId, code, phone: phone.replace(/\D/g, '') }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Verification failed');
      setChatToken(d.chat_token);
      // Default selection: if they chose "relative" in step 1, suggest relative.
      setMobileIs(role === 'relative' ? 'relative' : 'patient');
      setStep('whose');
    } catch (e) {
      setErr(e.message || 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  // ── STEP 3: Confirm whose mobile + (if relative) capture patient mobile ──
  const submitWhose = () => {
    setErr('');
    if (mobileIs === 'relative') {
      const p = (patientPhone || '').replace(/\D/g, '');
      if (p.length < 10) { setErr("Enter the patient's own 10-digit mobile."); return; }
    }
    setStep('chat');
  };

  // ── STEP 4: SEND MESSAGE ────────────────────────────────────────────────
  const send = async () => {
    setErr('');
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const body = { text: t };
      if (phoneToken) {
        body.phone_token = phoneToken;
      } else {
        // First message — include the full identity payload the backend expects.
        body.chat_token = chatToken;
        body.role = role;
        body.patient_name = name.trim();
        body.first_name = (patientFirst || '').trim();
        body.middle_name = (patientMiddle || '').trim();
        body.surname = (patientSurname || '').trim();
        body.verified_phone = phone.replace(/\D/g, '');
        body.mobile_is = mobileIs;
        if (mobileIs === 'relative') {
          body.patient_phone = (patientPhone || '').replace(/\D/g, '');
        }
      }
      const r = await fetch(`${apiBase}/api/public/chat/${slug}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Send failed');
      }
      const data = await r.json();
      if (data.phone_token && !phoneToken) {
        setPhoneToken(data.phone_token);
        try { localStorage.setItem(STORAGE_KEY, data.phone_token); } catch { /* private mode */ }
      }
      setMessages(m => [...m, data.message]);
      setText('');
    } catch (e) {
      setErr(e.message || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  // ── HEADER (shared across all steps) ────────────────────────────────────
  const header = (
    <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
      <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: stage.soft }}>
        <MessageCircle size={18} style={{ color: stage.accentDark }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-slate-900 text-sm">Chat with Dr. {firstName}</p>
        <p className="text-[11px] text-slate-500">Private · Mobile-verified · Encrypted at rest</p>
      </div>
      {/* Explicit X — backdrop click also closes but desktop users don't
          discover that pattern. Header-X is the universal escape hatch. */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close chat"
        className="shrink-0 p-1.5 -mr-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        data-testid="chat-close-btn"
      >
        <X size={18} />
      </button>
    </div>
  );

  // ── STEP RENDERERS ──────────────────────────────────────────────────────
  if (step === 'role') {
    return (
      <ModalShell onClose={onClose} testId="mock-b-chat-panel">
        <div className="-m-1 flex flex-col" style={{ minHeight: 380 }}>
          {header}
          <div className="flex-1 py-4 space-y-4" data-testid="chat-step-role">
            {authIsPatient && (authUser?.name || authPhoneDigits) && (
              <div className="rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 flex items-center gap-2" data-testid="chat-signed-in-chip">
                <CheckCircle size={14} className="text-sky-600 shrink-0" />
                <p className="text-[11px] text-sky-800">
                  Signed in as <b>{authUser?.name || authPhoneDigits}</b> — name &amp; mobile pre-filled.
                </p>
              </div>
            )}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Who is messaging?</p>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setRole('patient')}
                        className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition ${role === 'patient' ? 'border-sky-600 bg-sky-50 text-sky-800' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
                        data-testid="chat-role-patient-btn">Patient</button>
                <button onClick={() => setRole('relative')}
                        className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition ${role === 'relative' ? 'border-sky-600 bg-sky-50 text-sky-800' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
                        data-testid="chat-role-relative-btn">Patient's Relative</button>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">
                First name <span className="text-rose-600">*</span>
              </label>
              <input value={patientFirst} onChange={(e) => setPatientFirst(e.target.value)} placeholder="e.g. Aarav"
                     maxLength={40}
                     className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 outline-none"
                     data-testid="chat-role-first-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Middle <span className="text-slate-400 font-normal">(optional)</span></label>
                <input value={patientMiddle} onChange={(e) => setPatientMiddle(e.target.value)} placeholder="e.g. Raj"
                       maxLength={40}
                       className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 outline-none"
                       data-testid="chat-role-middle-name-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Surname <span className="text-amber-600 font-medium">(please share)</span></label>
                <input value={patientSurname} onChange={(e) => setPatientSurname(e.target.value)} placeholder="e.g. Patel"
                       maxLength={40}
                       className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 outline-none"
                       data-testid="chat-role-surname-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Mobile (10 digits)</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Mobile" inputMode="tel" maxLength={12}
                     className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 outline-none"
                     data-testid="chat-role-phone-input" />
              <p className="text-[10px] text-slate-400 mt-1">We'll send a verification code to confirm your number. Existing patients skip this step.</p>
            </div>
            {showEmailField && (
              <div data-testid="chat-role-email-fallback">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Email (SMS failed — send code via email instead)</label>
                <input value={emailFallback} onChange={(e) => setEmailFallback(e.target.value)} placeholder="you@example.com" type="email"
                       className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 outline-none"
                       data-testid="chat-role-email-input" />
              </div>
            )}
            {err && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2" data-testid="chat-role-error">{err}</div>}
          </div>
          <div className="pt-3 border-t border-slate-100">
            <button onClick={submitRole} disabled={busy}
                    className="w-full rounded-full py-3 text-white text-sm font-semibold disabled:opacity-50"
                    style={{ backgroundColor: stage.accent }}
                    data-testid="chat-role-continue-btn">
              {busy ? <Loader size={16} className="animate-spin inline mr-2" /> : null}
              Continue
            </button>
          </div>
        </div>
      </ModalShell>
    );
  }

  if (step === 'wall') {
    const returnPath = `/dr/${slug}/preview-b`;
    const loginHref = `/login?return=${encodeURIComponent(returnPath)}&role=patient`;
    const registerHref = `/register?return=${encodeURIComponent(returnPath)}&role=patient`;
    return (
      <ModalShell onClose={onClose} testId="mock-b-chat-panel">
        <div className="-m-1 flex flex-col" style={{ minHeight: 380 }}>
          {header}
          <div className="flex-1 py-6 space-y-4 text-center" data-testid="chat-step-wall">
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
              <Lock size={24} className="text-amber-700" />
            </div>
            <div>
              <p className="font-semibold text-slate-900 text-base">You've chatted with {wallDoctorName || `Dr. ${firstName}`} before</p>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed max-w-xs mx-auto">
                To continue this conversation or start a new one, please sign in or register a patient profile.
                Your previous chat will be linked to your account.
              </p>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-left mx-2">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Why this step?</p>
              <p className="text-[11px] text-slate-600 leading-relaxed">Top doctors get hundreds of chat requests. Registered patients get priority replies, plan-based message limits, and a single secure inbox across all your doctors.</p>
            </div>
          </div>
          <div className="pt-3 border-t border-slate-100 grid grid-cols-2 gap-2">
            <a href={loginHref}
               className="rounded-full py-3 text-center text-sm font-semibold border border-slate-300 text-slate-800 hover:bg-slate-50"
               data-testid="chat-wall-login-btn">
              Log in
            </a>
            <a href={registerHref}
               className="rounded-full py-3 text-center text-sm font-semibold text-white"
               style={{ backgroundColor: stage.accent }}
               data-testid="chat-wall-register-btn">
              Register
            </a>
          </div>
        </div>
      </ModalShell>
    );
  }

  if (step === 'limit') {
    const cap = limitInfo?.cap ?? 3;
    const used = limitInfo?.used ?? cap;
    const days = limitInfo?.window_days ?? 365;
    const windowLabel = days >= 365 ? `${Math.round(days / 365)} year${days >= 730 ? 's' : ''}` : `${days} days`;
    return (
      <ModalShell onClose={onClose} testId="mock-b-chat-panel">
        <div className="-m-1 flex flex-col" style={{ minHeight: 380 }}>
          {header}
          <div className="flex-1 py-6 space-y-4 text-center" data-testid="chat-step-limit">
            <div className="mx-auto w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center">
              <Lock size={24} className="text-rose-700" />
            </div>
            <div>
              <p className="font-semibold text-slate-900 text-base">You've used all your free chats with {wallDoctorName || `Dr. ${firstName}`}</p>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed max-w-xs mx-auto">
                You've completed <b>{used}</b> chat session{used === 1 ? '' : 's'} (your plan allows {cap} in {windowLabel}).
                For continued care, please book an appointment — or upgrade your patient plan to chat more.
              </p>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-left mx-2">
              <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-1">A note from Dr. {firstName}</p>
              <p className="text-[11px] text-amber-900 leading-relaxed">Chat is best for short, one-off questions. For ongoing care or detailed follow-ups, an in-clinic visit gives you the time you deserve.</p>
            </div>
          </div>
          <div className="pt-3 border-t border-slate-100 grid grid-cols-2 gap-2">
            <button onClick={onClose}
                    className="rounded-full py-3 text-center text-sm font-semibold border border-slate-300 text-slate-800 hover:bg-slate-50"
                    data-testid="chat-limit-close-btn">
              Close
            </button>
            <a href={`/dr/${slug}/preview-b#book`}
               className="rounded-full py-3 text-center text-sm font-semibold text-white"
               style={{ backgroundColor: stage.accent }}
               data-testid="chat-limit-book-btn">
              Book a visit
            </a>
          </div>
        </div>
      </ModalShell>
    );
  }

  if (step === 'otp') {
    return (
      <ModalShell onClose={onClose} testId="mock-b-chat-panel">
        <div className="-m-1 flex flex-col" style={{ minHeight: 380 }}>
          {header}
          <div className="flex-1 py-5 space-y-4" data-testid="chat-step-otp">
            <div>
              <p className="text-sm font-semibold text-slate-900">Enter the {otpLength}-digit code</p>
              <p className="text-xs text-slate-500 mt-1">
                Sent via <b>{otpChannel === 'email' ? 'email' : 'SMS'}</b> to <b>{otpTarget}</b>. Expires in 10 minutes.
              </p>
            </div>
            <input value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, otpLength))}
                   placeholder={'•'.repeat(otpLength)} inputMode="numeric" maxLength={otpLength}
                   className="w-full rounded-xl border border-slate-300 px-3 py-3 text-center text-2xl font-mono tracking-[0.4em] focus:ring-2 focus:ring-sky-500/30 outline-none"
                   data-testid="chat-otp-code-input" />
            {err && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2" data-testid="chat-otp-error">{err}</div>}
            <button onClick={() => setStep('role')} className="text-xs text-slate-500 hover:text-slate-800" data-testid="chat-otp-resend-btn">← Change mobile / resend</button>
          </div>
          <div className="pt-3 border-t border-slate-100">
            <button onClick={submitOtp} disabled={busy || otpCode.length !== otpLength}
                    className="w-full rounded-full py-3 text-white text-sm font-semibold disabled:opacity-50"
                    style={{ backgroundColor: stage.accent }}
                    data-testid="chat-otp-verify-btn">
              {busy ? <Loader size={16} className="animate-spin inline mr-2" /> : null}
              Verify
            </button>
          </div>
        </div>
      </ModalShell>
    );
  }

  if (step === 'whose') {
    return (
      <ModalShell onClose={onClose} testId="mock-b-chat-panel">
        <div className="-m-1 flex flex-col" style={{ minHeight: 380 }}>
          {header}
          <div className="flex-1 py-5 space-y-4" data-testid="chat-step-whose">
            <div>
              <p className="text-sm font-semibold text-slate-900">Whose mobile did you just verify?</p>
              <p className="text-xs text-slate-500 mt-1">This helps Dr. {firstName} pull up the right patient record.</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setMobileIs('patient')}
                      className={`rounded-xl border px-3 py-3 text-sm font-semibold transition ${mobileIs === 'patient' ? 'border-sky-600 bg-sky-50 text-sky-800' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
                      data-testid="chat-whose-patient-btn">Patient's mobile</button>
              <button onClick={() => setMobileIs('relative')}
                      className={`rounded-xl border px-3 py-3 text-sm font-semibold transition ${mobileIs === 'relative' ? 'border-sky-600 bg-sky-50 text-sky-800' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
                      data-testid="chat-whose-relative-btn">Relative's mobile</button>
            </div>
            {mobileIs === 'relative' && (
              <div data-testid="chat-whose-patient-phone-block">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Patient's own mobile (10 digits)</label>
                <input value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)} placeholder="Patient mobile" inputMode="tel" maxLength={12}
                       className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 outline-none"
                       data-testid="chat-whose-patient-phone-input" />
                <p className="text-[10px] text-slate-400 mt-1">No second OTP needed — we use this to match against Dr. {firstName}'s records.</p>
              </div>
            )}
            {err && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2" data-testid="chat-whose-error">{err}</div>}
          </div>
          <div className="pt-3 border-t border-slate-100">
            <button onClick={submitWhose}
                    className="w-full rounded-full py-3 text-white text-sm font-semibold"
                    style={{ backgroundColor: stage.accent }}
                    data-testid="chat-whose-continue-btn">
              Open chat
            </button>
          </div>
        </div>
      </ModalShell>
    );
  }

  // step === 'chat' — free messaging
  return (
    <ModalShell onClose={onClose} testId="mock-b-chat-panel">
      <div className="-m-1 flex flex-col" style={{ minHeight: 480, maxHeight: '80vh' }}>
        {header}

        <div ref={scrollRef} className="flex-1 overflow-y-auto py-4 space-y-2.5" data-testid="mock-b-chat-messages">
          {!loaded && (
            <p className="text-xs text-center text-slate-400 py-6">Loading…</p>
          )}
          {loaded && messages.length === 0 && (
            <div className="text-center py-6">
              <p className="text-sm text-slate-700 font-medium">Send Dr. {firstName} a message</p>
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                Briefly describe your concern. Dr. {firstName} typically replies within a few hours during clinic hours.
              </p>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender === 'patient' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm leading-snug ${m.sender === 'patient' ? 'text-white rounded-br-sm' : 'bg-slate-100 text-slate-800 rounded-bl-sm'}`}
                style={m.sender === 'patient' ? { backgroundColor: stage.accent } : {}}>
                {m.text}
                <div className={`text-[10px] mt-1 flex items-center gap-1 ${m.sender === 'patient' ? 'text-white/70 justify-end' : 'text-slate-400'}`}>
                  <span>{new Date(m.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  {/* Read receipts: single tick = sent to server, double = doctor opened thread.
                      Only shown on the patient's own messages (right-aligned bubbles). */}
                  {m.sender === 'patient' && (
                    m.read_at ? (
                      <span
                        title={`Seen ${new Date(m.read_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                        className="text-sky-200 font-bold tracking-tighter"
                        data-testid={`chat-msg-read-${m.id}`}
                      >✓✓</span>
                    ) : (
                      <span
                        title="Sent"
                        className="text-white/60 font-bold"
                        data-testid={`chat-msg-sent-${m.id}`}
                      >✓</span>
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {err && (
          <div className="mt-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2" data-testid="mock-b-chat-error">{err}</div>
        )}

        <div className="flex items-end gap-2 pt-3">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
            placeholder={`Type a message to Dr. ${firstName}…`}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm resize-none focus:ring-2 focus:ring-sky-500/20 outline-none"
            data-testid="mock-b-chat-input" />
          <button onClick={send} disabled={sending || !text.trim()}
            className="rounded-full p-3 text-white shrink-0 disabled:opacity-50"
            style={{ backgroundColor: stage.accent }}
            data-testid="mock-b-chat-send-btn">
            {sending ? <Loader size={16} className="animate-spin" /> : <ArrowRight size={16} />}
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-2 text-center">
          For medical emergencies, please call your nearest hospital — this is not a real-time service.
        </p>
      </div>
    </ModalShell>
  );
}

function TurboLockedModal({ stage, firstName, title, body, onClose }) {
  return (
    <ModalShell onClose={onClose} testId="mock-b-turbo-locked-modal">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-100">
          <Lock size={18} className="text-amber-700" />
        </div>
        <h3 className="font-semibold text-slate-900 text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>{title}</h3>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mt-4">
        <p className="text-[11px] uppercase tracking-widest font-bold text-amber-700 mb-1">For Dr. {firstName}</p>
        <p className="text-xs text-amber-900">Upgrade to <strong>Turbo</strong> in your DocSite dashboard to unlock direct calls + private chat + WhatsApp masking.</p>
      </div>
      <div className="flex justify-end mt-5">
        <button onClick={onClose} className="rounded-full px-5 py-2 text-sm font-semibold text-white" style={{ backgroundColor: stage.accent }}>
          Got it
        </button>
      </div>
    </ModalShell>
  );
}

